import { GalleryPreviewImage, GalleryItemsPreview, SubmitPanel } from "./features/inscribe/InscriptionReview";
import { isHtmlContentType } from "../shared/gallery-content";
import { GALLERY_FILE_ACCEPT, useGalleryFile } from "./features/inscribe/gallery-file";
import { LocalInscriptionDocument } from "./components/InscriptionDocumentFrame";
import { readStudioHandoff } from "./lib/studio-handoff";
import { HomePage } from "./features/home/HomePage";
import { RouteErrorBoundary } from "./components/RouteErrorBoundary";
import { resumeRecoveryPackage } from "./lib/recovery-api";
import { RecoveryPanel } from "./features/workspace/RecoveryPanel";
import { InscriptionHelp } from "./features/workspace/Experience";
import "./features/workspace/workspace.css";
import {
  ArrowRight,
  Bitcoin,
  Check,
  CheckCircle2,
  ChevronDown,
  Clipboard,
  Coins,
  Copy,
  Download,
  ExternalLink,
  FileArchive,
  FileJson,
  FileText,
  Fingerprint,
  Gauge,
  History as HistoryIcon,
  Image as ImageIcon,
  Info,
  LayoutGrid,
  Layers3,
  Link2,
  LoaderCircle,
  Menu,
  Plus,
  Radio,
  Search,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Upload,
  WalletCards,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  type ChangeEvent,
  type DragEvent,
  type FormEvent,
  type ReactNode,
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import { AccountControl, AccountWalletSelector } from "./account-control";
import { useAppState } from "./app-state";
import { ResilientInscriptionMedia } from "./components/ResilientInscriptionMedia";
import { StcFooterLogo } from "./footer-logo";
import {
  DelegateDistribution,
} from "./features/inscribe/DelegateDistribution";
import { DestinationCsvImport } from "./features/inscribe/DestinationCsvImport";
import { InscriptionFeeSummary as FeeSummary } from "./features/inscribe/InscriptionFeeSummary";
import { RareSatPicker } from "./features/inscribe/RareSatPicker";
import { SpecificSatPicker } from "./features/inscribe/SpecificSatPicker";
import { useWalletRareSatSelection } from "./features/inscribe/rare-sat-selection";
import { useSpecificSatSelection } from "./features/inscribe/specific-sat-selection";
import { TemporaryRuneClaimPage } from "./features/inscribe/TemporaryRuneClaimPage";
import { RunesPage } from "./features/inscribe/RunesPage";
import { resizeDelegateRecipients } from "./features/inscribe/delegate-distribution";
import {
  broadcastTransaction,
  completeHiveUpload,
  fetchAdminStats,
  fetchAdminWallet,
  fetchOrdinalGalleryContent,
  resolveImageInscription,
  fetchInscriptionHistory,
  fetchInscriptionTransactionStatuses,
  requestHiveUploadAuthorizations,
  requestAdminChallenge,
  requestAdminSession,
  resolveWalletSatSources,
  resolveReinscription,
} from "./lib/api";
import type { AdminInscriptionJob, AdminStatsPage, AdminUserRecord, AdminWalletPage } from "./lib/admin";
import { AdminWalletFeeTotal } from "./features/inscribe/AdminWalletFeeTotal";
import {
  applyBatchYaml,
  applyBulkMetadataPaste,
  batchPayloadLimitMessage,
  batchRareSatSelectionLimit,
  buildBulkMetadataTemplate,
  filterBatchFilesForCompression,
  MAX_BATCH_ITEMS,
  MAX_INSCRIPTION_FILE_BYTES,
  parseDelegateBatchYaml,
} from "./lib/batch";
import { buildBrc20Payload, createBrc20Drafts, type Brc20Fields, type Brc20Operation } from "./lib/brc20";
import { brotliCompress, propertyTags } from "./lib/content";
import { copyText } from "./lib/clipboard";
import { applyDestinationAddresses } from "./lib/destination-csv";
import {
  buildHiveReferenceBody,
  HIVE_IMAGE_ACCEPT,
  uploadHiveFileDirect,
  validateHiveImageFile,
  type HiveUploadAuthorization,
  type HiveUploadReceipt,
} from "./lib/hive";
import { GALLERY_INPUT_EXAMPLE, INSCRIPTION_ID_PATTERN, parseGalleryInput } from "./lib/gallery";
import { DEFAULT_POSTAGE } from "./lib/inscriptions";
import { resolveParentSelection } from "./lib/parents";
import { inscriptionExplorerUrl, transactionExplorerUrl } from "./lib/explorer-links";
import {
  applyInscriptionTransactionStatuses,
  buildInscriptionIds,
  excludeInscriptionHistory,
  inscriptionHistoryStatus,
  mergeInscriptionHistory,
  readLocalInscriptionHistory,
  removeLocalInscriptionHistory,
  saveLocalInscriptionHistory,
  type InscriptionHistoryRecord,
} from "./lib/history";
import {
  createHistoryLaunchpadPrefill,
  HISTORY_LAUNCHPAD_PREFILL_KEY,
} from "./lib/history-launchpad";
import type {
  ConnectedWallet,
  InscriptionAttributes,
  InscriptionDraft,
  RevealOptions,
  SignedPackage,
  TraitValue,
  WalletProviderId,
} from "./lib/types";
import {
  shortenAddress,
  signWalletOwnershipMessage,
  walletAvailability,
} from "./lib/wallets";
import { downloadSignedPackage } from "./lib/workflow";
const FinanceApp = lazy(() => import("./features/finance/FinanceApp").then((module) => ({ default: module.FinanceApp })));
const DexApp = lazy(() => import("./features/finance/FinanceApp").then((module) => ({ default: module.DexApp })));
const FreeplayDexApp = lazy(() => import("./features/finance/FinanceApp").then((module) => ({ default: module.FreeplayDexApp })));
const AuctionsApp = lazy(() => import("./features/auctions/AuctionsApp").then((module) => ({ default: module.AuctionsApp })));
const LaunchpadApp = lazy(() => import("./features/launchpad/LaunchpadApp").then((module) => ({ default: module.LaunchpadApp })));
const ShieldedHeroesApp = lazy(() => import("./features/marketplace/ShieldedHeroesApp").then((module) => ({ default: module.ShieldedHeroesApp })));
const ShieldedHauntlingsApp = lazy(() => import("./features/shielded-v2/ShieldedHauntlingsApp").then((module) => ({ default: module.ShieldedHauntlingsApp })));
const ShieldHubPage = lazy(() => import("./features/shielded-v2/ShieldHubPage").then((module) => ({ default: module.ShieldHubPage })));
const OrdDropzApp = lazy(() => import("./features/marketplace/OrdDropzApp").then(module => ({ default: module.OrdDropzApp })));
const ShieldedAboutPage = lazy(() => import("./features/shielded-v2/ShieldedAboutPage").then((module) => ({ default: module.ShieldedAboutPage })));
const ShieldedTechnicalPage = lazy(() => import("./features/shielded-v2/ShieldedTechnicalPage").then((module) => ({ default: module.ShieldedTechnicalPage })));
const ShieldedNodePage = lazy(() => import("./features/shielded-v2/ShieldedNodePage").then((module) => ({ default: module.ShieldedNodePage })));
const MarketplaceApp = lazy(() => import("./features/marketplace/MarketplaceApp").then((module) => ({ default: module.MarketplaceApp })));
const RareSatsApp = lazy(() => import("./features/marketplace/RareSatsApp").then((module) => ({ default: module.RareSatsApp })));
const BitmapApp = lazy(() => import("./features/marketplace/BitmapApp").then((module) => ({ default: module.BitmapApp })));
const StudioApp = lazy(() => import("./features/studio/StudioApp").then((module) => ({ default: module.StudioApp })));
const ToolsApp = lazy(() => import("./features/tools/ToolsApp").then((module) => ({ default: module.ToolsApp })));
const WorldsPage = lazy(() => import("./features/worlds/WorldsPage").then((module) => ({ default: module.WorldsPage })));
const FeatureTweetAdminPage = lazy(() => import("./features/social/FeatureTweetAdminPage").then((module) => ({ default: module.FeatureTweetAdminPage })));
const PartnerInscriberPage = lazy(() => import("./features/inscribe/PartnerInscriberPage").then((module) => ({ default: module.PartnerInscriberPage })));
const PartnerInscriberDocsPage = lazy(() => import("./features/inscribe/PartnerInscriberDocsPage").then((module) => ({ default: module.PartnerInscriberDocsPage })));
const PartnerInscriberThemePage = lazy(() => import("./features/inscribe/PartnerInscriberThemePage").then((module) => ({ default: module.PartnerInscriberThemePage })));
const ShieldedOrdinalMintPage = lazy(() => import("./features/inscribe/ShieldedOrdinalMintPage").then((module) => ({ default: module.ShieldedOrdinalMintPage })));
import { WORLDS_ROUTE } from "./features/worlds/worlds-draft";
import { INSCRIBE_NAV_ITEMS, PUBLIC_NAV_ITEMS, publicNavItemIsActive } from "./navigation";

const numberFormatter = new Intl.NumberFormat("en-GB");

type Tone = "success" | "warning" | "error" | "neutral";

function sats(value: number): string {
  return `${numberFormatter.format(Math.max(0, Math.round(value)))} sats`;
}

function bytes(value: number): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 / 1024).toFixed(2)} MB`;
}

function parseMetadata(raw: string): unknown {
  if (!raw.trim()) return undefined;
  return JSON.parse(raw) as unknown;
}

type TraitInputType = "text" | "number" | "boolean" | "null";

interface TraitRowValue {
  id: string;
  name: string;
  value: string;
  type: TraitInputType;
}

function newTraitRow(): TraitRowValue {
  return { id: crypto.randomUUID(), name: "", value: "", type: "text" };
}

function attributesFromRows(rows: TraitRowValue[], title: string): InscriptionAttributes | undefined {
  const usedNames = new Set<string>();
  const traits: Record<string, TraitValue> = {};
  for (const row of rows) {
    const name = row.name.trim();
    const hasValue = row.type === "null" || Boolean(row.value.trim());
    if (!name && !hasValue) continue;
    if (!name) throw new Error("Every trait needs a name");
    if (usedNames.has(name)) throw new Error(`Trait “${name}” is listed twice`);
    usedNames.add(name);

    let value: TraitValue;
    if (row.type === "null") value = null;
    else if (row.type === "boolean") value = row.value === "true";
    else if (row.type === "number") {
      if (!/^-?\d+$/.test(row.value.trim())) throw new Error(`Trait “${name}” needs a whole number`);
      value = Number(row.value);
      if (!Number.isSafeInteger(value)) throw new Error(`Trait “${name}” is too large`);
    } else value = row.value;
    traits[name] = value;
  }
  if (!title.trim() && Object.keys(traits).length === 0) return undefined;
  return { title: title.trim() || undefined, traits: Object.keys(traits).length ? traits : undefined };
}

function shortId(value: string): string {
  return value.length > 20 ? `${value.slice(0, 9)}…${value.slice(-7)}` : value;
}

function BrandMark({ small = false }: { small?: boolean }) {
  return (
    <span className={small ? "brand-mark small" : "brand-mark"} aria-hidden="true">
      <Link2 />
    </span>
  );
}

function Notice({ tone = "neutral", children }: { tone?: Tone; children: ReactNode }) {
  return (
    <div className={`notice ${tone}`} role={tone === "error" ? "alert" : undefined}>
      {tone === "error" ? <Info size={17} /> : tone === "success" ? <CheckCircle2 size={17} /> : <ShieldCheck size={17} />}
      <span>{children}</span>
    </div>
  );
}

function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="icon-button"
      type="button"
      title={label}
      onClick={() => {
        void copyText(value).then((success) => {
          if (!success) return;
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1300);
        });
      }}
    >
      {copied ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );
}

function AppHeader() {
  const { config, wallet } = useAppState();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const adminAddress = config?.adminAddress.toLowerCase();
  const isAdmin = Boolean(adminAddress && wallet && (
    wallet.payment.address.toLowerCase() === adminAddress
    || wallet.ordinals.address.toLowerCase() === adminAddress
  ));

  return (
    <header className="topbar">
      <Link to="/" className="brand" aria-label="Spam The Chain home">
        <span className="brand-word">SPAM THE</span>
        <span className="brand-tagline">CHAIN</span>
      </Link>
      <nav id="main-navigation" className={menuOpen ? "main-nav open" : "main-nav"} aria-label="Main navigation">
        {PUBLIC_NAV_ITEMS.map((item) => item.disabled
          ? <span className="nav-disabled" aria-disabled="true" title={`${item.label} is not public yet`} key={item.to}>{item.label}{item.status ? <small>{item.status}</small> : null}</span>
          : (
              <Link
                to={item.to}
                className={publicNavItemIsActive(pathname, item) ? "active" : undefined}
                aria-current={publicNavItemIsActive(pathname, item) ? "page" : undefined}
                onClick={() => setMenuOpen(false)}
                key={item.to}
              >
                {item.label}
              </Link>
            ))}
        {isAdmin ? <NavLink to="/stats" onClick={() => setMenuOpen(false)}>Stats</NavLink> : null}
        {isAdmin ? <NavLink to="/admin/feature-tweets" onClick={() => setMenuOpen(false)}>Tweets</NavLink> : null}
      </nav>
      <div className="header-status">
        <AccountControl />
        <button className="menu-button" type="button" aria-label="Toggle navigation" aria-expanded={menuOpen} aria-controls="main-navigation" onClick={() => setMenuOpen((open) => !open)}>
          {menuOpen ? <X /> : <Menu />}
        </button>
      </div>
      {config && !config.feeConfigured && !config.marketplaceFeeAddress ? <span className="config-ribbon">SETUP REQUIRED</span> : null}
    </header>
  );
}

const INSCRIBE_TAB_ICONS = new Map<string, LucideIcon>([
  ["/inscribe", FileText],
  ["/batch", Layers3],
  ["/gallery", LayoutGrid],
  ["/runes", Coins],
  ["/history", HistoryIcon],
  ["/console", Radio],
]);

function InscribeTabs() {
  const { pathname } = useLocation();
  return (
    <nav className="inscribe-tabs-shell" aria-label="Inscription navigation">
      <div className="inscribe-tabs">
        {INSCRIBE_NAV_ITEMS.map((item) => {
          const Icon = INSCRIBE_TAB_ICONS.get(item.to) ?? FileText;
          const active = publicNavItemIsActive(pathname, item);
          return (
            <Link
              to={item.to}
              className={active ? "active" : undefined}
              aria-current={active ? "page" : undefined}
              key={item.to}
            >
              <Icon />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function InscribeRouteShell({ children }: { children: ReactNode }) {
  return (
    <>
      <InscribeTabs />
      {children}
    </>
  );
}


function WalletModal() {
  const {
    accountBusy,
    accountError,
    clearAccountError,
    connectPrimaryWallet,
    disconnectWallet,
    linkWallet,
    setWalletModalMode,
    setWalletModalOpen,
    wallet,
    walletModalMode,
    walletModalOpen,
  } = useAppState();
  const [connecting, setConnecting] = useState<WalletProviderId | null>(null);
  const [error, setError] = useState("");
  const availability = walletAvailability();
  if (!walletModalOpen) return null;

  const close = () => {
    setWalletModalOpen(false);
    setWalletModalMode("connect");
    clearAccountError();
  };

  const connect = async (provider: WalletProviderId) => {
    setConnecting(provider);
    setError("");
    try {
      if (walletModalMode === "link") await linkWallet(provider);
      else {
        await connectPrimaryWallet(provider);
        setWalletModalOpen(false);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Wallet connection failed");
    } finally {
      setConnecting(null);
    }
  };

  const providers: Array<{ id: WalletProviderId; name: string }> = [
    { id: "xverse", name: "Xverse" },
    { id: "unisat", name: "UniSat" },
  ];

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={close}>
      <section className="wallet-modal" role="dialog" aria-modal="true" aria-labelledby="wallet-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="modal-heading">
          <div><p className="eyebrow">{walletModalMode === "link" ? "ACCOUNT // WALLETS" : "CONNECT + VERIFY"}</p><h2 id="wallet-title">{walletModalMode === "link" ? "Add wallet" : "Connect wallet"}</h2></div>
          <button className="icon-button" type="button" aria-label="Close" onClick={close}><X /></button>
        </div>
        {wallet ? (
          <div className="connected-card">
            <CheckCircle2 />
            <span><strong>{walletModalMode === "link" ? "CURRENT WALLET" : wallet.provider === "xverse" ? "Xverse" : "UniSat"}</strong><small>VERIFIED · {shortenAddress(wallet.payment.address)}</small></span>
            {walletModalMode === "connect" ? <button type="button" className="text-button" onClick={disconnectWallet}>Sign out</button> : null}
          </div>
        ) : null}
        {!wallet || walletModalMode === "link" ? <div className="wallet-list">
          {providers.map((provider) => (
            <button key={provider.id} className="wallet-option" type="button" disabled={connecting !== null || accountBusy} onClick={() => void connect(provider.id)}>
              <span className="wallet-glyph"><Bitcoin size={21} /></span>
              <span><strong>{provider.name}</strong><small>{connecting === provider.id ? "SIGN TO VERIFY" : availability[provider.id] ? walletModalMode === "link" ? "ADD ANOTHER WALLET" : "READY" : "NOT FOUND"}</small></span>
              {connecting === provider.id ? <LoaderCircle className="spin" /> : availability[provider.id] ? <ArrowRight /> : <ExternalLink />}
            </button>
          ))}
        </div> : <p className="wallet-modal-help">Use the account menu to switch wallets or add another verified wallet.</p>}
        {error || accountError ? <Notice tone="error">{error || accountError}</Notice> : null}
      </section>
    </div>
  );
}

function PageIntro({ eyebrow, title, accent, description }: { eyebrow: string; title: string; accent: string; description?: string }) {
  return (
    <div className="page-intro">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title} <em>{accent}</em></h1>
      {description ? <p>{description}</p> : null}
    </div>
  );
}

function formattedMetadata(metadata: unknown): string {
  try {
    return JSON.stringify(metadata, null, 2) ?? String(metadata);
  } catch {
    return String(metadata);
  }
}

function DataPreview({ label, value, error, note }: { label: string; value?: unknown; error?: string; note?: string }) {
  if (value === undefined && !error) return null;
  return (
    <div className={error ? "data-preview invalid" : "data-preview"}>
      <div><span>{label}</span><strong>{error ? "INVALID" : "READY"}</strong></div>
      {error ? <p>{error}</p> : <pre>{formattedMetadata(value)}</pre>}
      {note ? <small>{note}</small> : null}
    </div>
  );
}

function ParentFields({
  primary,
  onPrimary,
  coParents,
  onCoParents,
}: {
  primary: string;
  onPrimary: (value: string) => void;
  coParents: string[];
  onCoParents: (value: string[]) => void;
}) {
  return (
    <div className="parent-id-fields full">
      <label className="field">
        <span>PRIMARY PARENT <small>OPTIONAL</small></span>
        <input value={primary} onChange={(event) => onPrimary(event.target.value)} placeholder="INSCRIPTION ID · …i0" />
      </label>
      {coParents.length ? <span className="co-parent-label">CO-PARENTS</span> : null}
      {coParents.map((value, index) => (
        <div className="co-parent-field" key={`co-parent-${index}`}>
          <label className="field">
            <span>CO-PARENT {index + 1}</span>
            <input value={value} onChange={(event) => onCoParents(coParents.map((candidate, candidateIndex) => candidateIndex === index ? event.target.value : candidate))} placeholder="INSCRIPTION ID · …i0" />
          </label>
          <button className="remove-co-parent" type="button" aria-label={`Remove co-parent ${index + 1}`} onClick={() => onCoParents(coParents.filter((_, candidateIndex) => candidateIndex !== index))}><X /></button>
        </div>
      ))}
      <button className="add-co-parent" type="button" onClick={() => onCoParents([...coParents, ""])}><Plus /> ADD CO-PARENT</button>
      <small className="parent-help">YOU MUST OWN EVERY PARENT. EACH ONE IS RETURNED TO YOUR WALLET.</small>
    </div>
  );
}

function DropZone({ onFiles, multiple = false, compact = false, limitLabel = "MAX 400 KB", accept = "image/*,audio/*,video/*,.txt,.html,.json,.svg,.pdf,application/octet-stream" }: { onFiles: (files: File[]) => void; multiple?: boolean; compact?: boolean; limitLabel?: string; accept?: string }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const handle = (files: FileList | null) => files && onFiles(Array.from(files));
  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    handle(event.dataTransfer.files);
  };
  return (
    <div className={compact ? "drop-zone compact" : "drop-zone"} onDragOver={(event) => event.preventDefault()} onDrop={drop}>
      <input ref={inputRef} type="file" hidden accept={accept} multiple={multiple} onChange={(event) => handle(event.target.files)} />
      <Upload />
      <div><strong>{multiple ? "DROP FILES" : "DROP FILE"}</strong><small>{limitLabel}</small></div>
      <button className="secondary-button" type="button" onClick={() => inputRef.current?.click()}>BROWSE</button>
    </div>
  );
}

interface PreparedHiveMedia extends HiveUploadReceipt {
  body: Uint8Array;
}

async function prepareHiveMedia(
  file: File,
  permit: HiveUploadAuthorization,
  authorization: string,
  clientInscriptionId: string,
  onProgress: (percent: number) => void,
): Promise<PreparedHiveMedia> {
  const signedReceipt = await uploadHiveFileDirect(file, permit, onProgress);
  const asset = await completeHiveUpload(authorization, permit.uploadId, signedReceipt);
  const body = await brotliCompress(buildHiveReferenceBody(clientInscriptionId, asset.assetId));
  return { ...asset, body };
}

function SinglePage() {
  const location = useLocation();
  const { fees, wallet, config, authorization, authorizeWallet } = useAppState();
  const rareSats = useWalletRareSatSelection(1);
  const [mode, setMode] = useState<"file" | "hive" | "text" | "delegate">("file");
  const [file, setFile] = useState<File | null>(null);
  const [body, setBody] = useState("");
  const [delegateId, setDelegateId] = useState("");
  const [delegateQuantity, setDelegateQuantity] = useState(1);
  const [delegateRecipients, setDelegateRecipients] = useState<string[]>([""]);
  const [delegateCsvApplied, setDelegateCsvApplied] = useState(false);
  const [fileBytes, setFileBytes] = useState<Uint8Array>();
  const [preparedBody, setPreparedBody] = useState<Uint8Array>();
  const [compressing, setCompressing] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [postage, setPostage] = useState(DEFAULT_POSTAGE);
  const [metadata, setMetadata] = useState("");
  const [attributeTitle, setAttributeTitle] = useState("");
  const [traitRows, setTraitRows] = useState<TraitRowValue[]>(() => [newTraitRow()]);
  const [parentId, setParentId] = useState("");
  const [coParentIds, setCoParentIds] = useState<string[]>([]);
  const [sourceMode, setSourceMode] = useState<"none" | "sat" | "reinscribe">("none");
  const [reinscriptionId, setReinscriptionId] = useState("");
  const [brotli, setBrotli] = useState(false);
  const [vanityStart, setVanityStart] = useState("");
  const [vanityEnd, setVanityEnd] = useState("");
  const [ephemeral, setEphemeral] = useState(false);
  const [opReturn, setOpReturn] = useState(false);
  const [opReturnData, setOpReturnData] = useState("");
  const [burn, setBurn] = useState(false);
  const [burnMessage, setBurnMessage] = useState("");
  const [feeRate, setFeeRate] = useState(12);
  const [readError, setReadError] = useState("");
  const [hiveMedia, setHiveMedia] = useState<PreparedHiveMedia | null>(null);
  const [hiveProgress, setHiveProgress] = useState(0);
  const [hiveUploading, setHiveUploading] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  useEffect(() => {
    if(new URLSearchParams(location.search).get('source')!=='studio')return;
    try {const handoff=readStudioHandoff();if(!handoff)return;setFile(handoff.file);setFileBytes(handoff.bytes);setAttributeTitle(handoff.title);setMetadata(handoff.metadata);setAdvanced(true);}
    catch(e){setReadError(e instanceof Error?e.message:'Studio edition could not load');}
  },[location.search]);
  const previewUrl = useMemo(() => file?.type.startsWith("image/") ? URL.createObjectURL(file) : "", [file]);
  const encodedText = useMemo(() => new TextEncoder().encode(body), [body]);
  const rawBody = mode === "text" ? encodedText : mode === "file" ? fileBytes : undefined;

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  useEffect(() => { if (fees) setFeeRate(fees.halfHourFee); }, [fees]);
  useEffect(() => {
    let cancelled = false;
    if (!rawBody || mode === "delegate") { setPreparedBody(undefined); return; }
    if (!brotli) { setPreparedBody(rawBody); return; }
    setCompressing(true);
    void brotliCompress(rawBody).then((value) => { if (!cancelled) setPreparedBody(value); }).catch(() => { if (!cancelled) setReadError("Brotli compression could not be prepared."); }).finally(() => { if (!cancelled) setCompressing(false); });
    return () => { cancelled = true; };
  }, [brotli, mode, rawBody]);

  const chooseFiles = (files: File[]) => {
    const next = files[0];
    if (!next) return;
    if (next.size > MAX_INSCRIPTION_FILE_BYTES) return setReadError("Files must be 400 KB or smaller.");
    setReadError(""); setFile(next);
    void next.arrayBuffer().then((buffer) => setFileBytes(new Uint8Array(buffer)));
  };

  const chooseHiveFile = (files: File[]) => {
    const next = files[0];
    if (!next) return;
    const validationError = validateHiveImageFile(next);
    if (validationError) return setReadError(validationError);
    setReadError("");
    setFile(next);
    setFileBytes(undefined);
    setHiveMedia(null);
    setHiveProgress(0);
  };

  const uploadSingleToHive = async (): Promise<PreparedHiveMedia> => {
    if (hiveMedia) return hiveMedia;
    if (!file) throw new Error("Choose Hive media first");
    if (!config?.hiveMediaEnabled || !config.hiveMediaClientInscriptionId) {
      throw new Error("Hive media is not configured yet.");
    }
    setReadError("");
    setHiveUploading(true);
    setHiveProgress(0);
    try {
      const session = authorization ?? await authorizeWallet();
      if (!session) throw new Error("Connect and verify your wallet to upload to Hive");
      const [permit] = await requestHiveUploadAuthorizations(session, [file]);
      if (!permit) throw new Error("Hive did not authorize the upload");
      const prepared = await prepareHiveMedia(file, permit, session, config.hiveMediaClientInscriptionId, setHiveProgress);
      setHiveMedia(prepared);
      return prepared;
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Hive upload failed";
      setReadError(message);
      throw caught instanceof Error ? caught : new Error(message);
    } finally {
      setHiveUploading(false);
    }
  };

  const parsedAdvanced = useMemo(() => {
    try {
      return { metadataValue: parseMetadata(metadata), attributesValue: attributesFromRows(traitRows, attributeTitle), advancedError: "" };
    } catch (caught) {
      return { metadataValue: undefined, attributesValue: undefined, advancedError: caught instanceof Error ? caught.message : "Advanced data is invalid" };
    }
  }, [attributeTitle, metadata, traitRows]);
  const { metadataValue, attributesValue, advancedError } = parsedAdvanced;
  const selectedWalletSat = rareSats.selectedSources[0];
  const revealSettingCount = [mode === "hive" || brotli, ephemeral, opReturn, burn, Boolean(vanityStart.trim()), Boolean(vanityEnd.trim())].filter(Boolean).length;
  const hasSource = mode === "delegate" ? Boolean(delegateId.trim()) : mode === "hive" ? Boolean(hiveMedia?.body.length) : Boolean(preparedBody?.length);
  const draft = useMemo<InscriptionDraft[]>(() => {
    if (!hasSource) return [];
    if (mode === "delegate") {
      return Array.from({ length: delegateQuantity }, (_, index) => ({
        id: `delegate-${index + 1}`,
        name: delegateQuantity === 1 ? "delegated-inscription" : `delegated-inscription-${index + 1}`,
        delegateId: delegateId.trim(),
        recipient: delegateRecipients[index]?.trim() || undefined,
        metadata: metadataValue,
        attributes: attributesValue,
        postage,
      }));
    }
    return [{
      id: "single",
      name: mode === "file" || mode === "hive" ? file?.name ?? "inscription" : "spam-the-chain-note.txt",
      mimeType: mode === "hive" ? "text/html;charset=utf-8" : mode === "file" ? file?.type || "application/octet-stream" : "text/plain;charset=utf-8",
      body: mode === "hive" ? hiveMedia?.body : preparedBody,
      previewBody: mode === "file" && file?.type.startsWith("image/") ? fileBytes : undefined,
      previewFile: mode === "hive" && file?.type.startsWith("image/") ? file : undefined,
      previewMimeType: mode === "hive" ? file?.type : undefined,
      sourceLabel: mode === "hive" ? `HIVE · ${file?.type || "MEDIA"}` : undefined,
      sourceSize: mode === "hive" ? file?.size : undefined,
      contentEncoding: mode === "hive" || brotli ? "br" : undefined,
      recipient: recipient.trim() || undefined,
      metadata: metadataValue,
      attributes: attributesValue,
      postage,
    }];
  }, [attributesValue, brotli, delegateId, delegateQuantity, delegateRecipients, file, fileBytes, hasSource, hiveMedia, metadataValue, mode, postage, preparedBody, recipient]);
  const delegateError = mode === "delegate" && delegateId.trim() && !INSCRIPTION_ID_PATTERN.test(delegateId.trim())
    ? "Enter a valid delegate inscription ID."
    : mode === "delegate" && delegateQuantity > 1 && sourceMode !== "none"
      ? "Multiple delegates use automatic sats. Use Batch to assign a different rare sat to each inscription."
      : mode === "delegate" && burn
        ? "Delegate inscriptions cannot be combined with Burn here. Use the dedicated Burn tool instead."
        : "";

  const prepareSingleHiveDrafts = async (): Promise<InscriptionDraft[]> => {
    if (!file) throw new Error("Choose Hive media first");
    const prepared = await uploadSingleToHive();
    return [{
      id: "single",
      name: file.name,
      mimeType: "text/html;charset=utf-8",
      body: prepared.body,
      previewFile: file.type.startsWith("image/") ? file : undefined,
      previewMimeType: file.type || "application/octet-stream",
      sourceLabel: `HIVE · ${file.type || "MEDIA"}`,
      sourceSize: file.size,
      contentEncoding: "br",
      recipient: recipient.trim() || undefined,
      metadata: metadataValue,
      attributes: attributesValue,
      postage,
    }];
  };

  const resolveRevealOptions = async (connected: ConnectedWallet): Promise<RevealOptions> => {
    if (delegateError) throw new Error(delegateError);
    const parentPromise = resolveParentSelection(connected, parentId, coParentIds);
    let sourcePromise: Promise<RevealOptions["source"]> | undefined;
    if (sourceMode === "sat") {
      if (!selectedWalletSat) throw new Error("Choose a wallet sat");
      sourcePromise = resolveWalletSatSources(connected.ordinals.address, [selectedWalletSat]).then(([source]) => source);
    } else if (sourceMode === "reinscribe") {
      sourcePromise = resolveReinscription(connected.ordinals.address, reinscriptionId.trim());
    }
    const [parents, source] = await Promise.all([parentPromise, sourcePromise]);
    return {
      ...parents, source,
      vanityStart: vanityStart.trim() || undefined,
      vanityEnd: vanityEnd.trim() || undefined,
      ephemeralReveal: ephemeral,
      opReturnData: opReturn ? opReturnData : undefined,
      burn,
      burnMessage: burn ? burnMessage : undefined,
    };
  };

  return (
    <main className="page-shell inscription-page">
      <PageIntro eyebrow="INSCRIBE // 01" title="INSCRIBE" accent="" />
      <div className="workspace-grid approved-workspace">
        <section className="maker-card approved-maker">
          <InscriptionHelp />
          <div className="source-layout">
            <div className="mode-rail" role="tablist" aria-label="Inscription type">
              <button type="button" disabled={hiveUploading} className={mode === "file" ? "active" : ""} onClick={() => { setMode("file"); setFile(null); setFileBytes(undefined); setHiveMedia(null); setReadError(""); }}><ImageIcon /><span><strong>FILE</strong></span></button>
              <button type="button" disabled={hiveUploading} className={mode === "hive" ? "active hive-tab" : "hive-tab"} onClick={() => { setMode("hive"); setFile(null); setFileBytes(undefined); setHiveMedia(null); setReadError(""); }}><Radio /><span><strong>HIVE</strong></span></button>
              <button type="button" disabled={hiveUploading} className={mode === "text" ? "active" : ""} onClick={() => setMode("text")}><FileText /><span><strong>TEXT</strong></span></button>
              <button type="button" disabled={hiveUploading} className={mode === "delegate" ? "active" : ""} onClick={() => { setMode("delegate"); setBurn(false); }}><Layers3 /><span><strong>DELEGATE</strong></span></button>
            </div>
            <div className="source-surface">
              {mode === "file" ? file ? <div className="file-preview"><div className="preview-art">{previewUrl ? <img src={previewUrl} alt="Selected inscription preview" /> : <FileArchive />}</div><div className="file-details"><p className="eyebrow">Selected file</p><h3>{file.name}</h3><span>{file.type || "application/octet-stream"} · {bytes(file.size)}</span>{brotli && preparedBody ? <span className="compressed-note">Brotli {bytes(preparedBody.length)}</span> : null}</div><button className="icon-button danger" type="button" aria-label="Remove file" onClick={() => { setFile(null); setFileBytes(undefined); }}><Trash2 /></button></div> : <DropZone onFiles={chooseFiles} /> : null}
              {mode === "hive" ? file ? <div className={hiveMedia ? "file-preview hive-file ready" : "file-preview hive-file"}><div className="preview-art">{previewUrl ? <img src={previewUrl} alt="Selected Hive media preview" /> : <Radio />}</div><div className="file-details"><p className="eyebrow">Hive media</p><h3>{file.name}</h3><span>{file.type || "application/octet-stream"} · {bytes(file.size)}</span>{hiveMedia ? <span className="compressed-note">REFERENCE READY · {bytes(hiveMedia.body.length)} ON-CHAIN</span> : hiveUploading ? <span className="compressed-note">UPLOADING · {hiveProgress}%</span> : <span>UPLOADS WHEN YOU REVIEW</span>}</div><div className="hive-file-actions">{hiveMedia ? <span className="hive-ready"><CheckCircle2 /> READY</span> : null}<button className="icon-button danger" type="button" aria-label="Remove Hive media" disabled={hiveUploading} onClick={() => { setFile(null); setHiveMedia(null); setHiveProgress(0); }}><Trash2 /></button></div>{hiveUploading ? <span className="hive-progress"><i style={{ width: `${hiveProgress}%` }} /></span> : null}</div> : <DropZone onFiles={chooseHiveFile} accept={HIVE_IMAGE_ACCEPT} limitLabel="JPEG · PNG · WEBP · GIF · AVIF · MAX 25 MB" /> : null}
              {mode === "text" ? <label className="field"><span>TEXT</span><textarea rows={10} value={body} onChange={(event) => setBody(event.target.value)} placeholder="TYPE OR PASTE…" /><small>{bytes(encodedText.length)}</small></label> : null}
              {mode === "delegate" ? <DelegateDistribution
                delegateId={delegateId}
                quantity={delegateQuantity}
                recipients={delegateRecipients}
                defaultRecipient={wallet?.ordinals.address}
                onDelegateId={setDelegateId}
                onQuantity={(quantity) => {
                  setDelegateQuantity(quantity);
                  setDelegateRecipients((current) => resizeDelegateRecipients(current, quantity, delegateCsvApplied));
                  setDelegateCsvApplied(false);
                }}
                onRecipient={(index, value) => {
                  setDelegateRecipients((current) => current.map((recipient, recipientIndex) => recipientIndex === index ? value : recipient));
                  setDelegateCsvApplied(false);
                }}
                onCopyFirst={() => {
                  setDelegateRecipients((current) => current.map(() => current[0]?.trim() ?? ""));
                  setDelegateCsvApplied(false);
                }}
                importedRecipientCount={delegateCsvApplied ? delegateRecipients.length : 0}
                onImportRecipients={(addresses) => {
                  setDelegateRecipients(addresses);
                  setDelegateCsvApplied(true);
                }}
                onClearImportedRecipients={() => {
                  setDelegateRecipients(Array.from({ length: delegateQuantity }, () => ""));
                  setDelegateCsvApplied(false);
                }}
              /> : null}
              <div className="content-preview-line"><span>SELECTED</span><strong>{mode === "hive" && file ? file.name : hasSource ? draft[0]?.name : "—"}</strong></div>
            </div>
          </div>
          {mode === "hive" && !config?.hiveMediaEnabled ? <Notice tone="warning">HIVE MEDIA SETUP IN PROGRESS</Notice> : null}
          {readError ? <Notice tone="error">{readError}</Notice> : null}
          <button className="advanced-toggle" type="button" onClick={() => setAdvanced((open) => !open)}><span><SlidersHorizontal /> OPTIONS</span><ChevronDown className={advanced ? "rotate" : ""} /></button>
          {advanced ? <div className="advanced-stack">
            <section className="advanced-section"><div><h3>OUTPUT</h3></div><div className="advanced-grid">
              {mode !== "delegate" ? <label className="field"><span>RECIPIENT</span><input value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="CONNECTED WALLET" /></label> : null}
              <label className="field"><span>POSTAGE</span><div className="suffix-input"><input type="number" min={DEFAULT_POSTAGE} value={postage} onChange={(event) => setPostage(Math.max(DEFAULT_POSTAGE, Number(event.target.value)))} /><span>SATS</span></div></label>
              <label className="field full"><span>METADATA</span><textarea rows={4} value={metadata} onChange={(event) => setMetadata(event.target.value)} placeholder={'{"collection":"…"}'} /></label>
            </div></section>
            <section className="advanced-section"><div><h3>ATTRIBUTES</h3></div><div className="advanced-grid">
              <label className="field"><span>TITLE</span><input value={attributeTitle} onChange={(event) => setAttributeTitle(event.target.value)} /></label>
              <div className="trait-editor full">
                <span className="trait-editor-label">TRAITS</span>
                <div className="trait-list">
                  {traitRows.map((row, index) => (
                    <div className="trait-row" key={row.id}>
                      <input aria-label={`Trait ${index + 1} name`} value={row.name} onChange={(event) => setTraitRows((current) => current.map((candidate) => candidate.id === row.id ? { ...candidate, name: event.target.value } : candidate))} placeholder="TRAIT NAME" />
                      {row.type === "boolean" ? (
                        <select aria-label={`Trait ${index + 1} value`} value={row.value || "true"} onChange={(event) => setTraitRows((current) => current.map((candidate) => candidate.id === row.id ? { ...candidate, value: event.target.value } : candidate))}>
                          <option value="true">TRUE</option><option value="false">FALSE</option>
                        </select>
                      ) : <input aria-label={`Trait ${index + 1} value`} disabled={row.type === "null"} inputMode={row.type === "number" ? "numeric" : undefined} value={row.value} onChange={(event) => setTraitRows((current) => current.map((candidate) => candidate.id === row.id ? { ...candidate, value: event.target.value } : candidate))} placeholder={row.type === "null" ? "NULL" : "VALUE"} />}
                      <select aria-label={`Trait ${index + 1} type`} value={row.type} onChange={(event) => {
                        const type = event.target.value as TraitInputType;
                        setTraitRows((current) => current.map((candidate) => candidate.id === row.id ? { ...candidate, type, value: type === "boolean" ? "true" : type === "null" ? "" : candidate.value } : candidate));
                      }}>
                        <option value="text">TEXT</option><option value="number">NUMBER</option><option value="boolean">BOOLEAN</option><option value="null">NULL</option>
                      </select>
                      <button className="remove-trait-button" type="button" aria-label={`Remove trait ${index + 1}`} onClick={() => setTraitRows((current) => current.filter((candidate) => candidate.id !== row.id))}><X /></button>
                    </div>
                  ))}
                </div>
                <button className="add-trait-button" type="button" onClick={() => setTraitRows((current) => [...current, newTraitRow()])}><Plus /> ADD TRAIT</button>
              </div>
            </div></section>
            <DataPreview
              label="ON-CHAIN DATA PREVIEW"
              value={metadataValue !== undefined || attributesValue !== undefined ? {
                ...(metadataValue !== undefined ? { metadata: metadataValue } : {}),
                ...(attributesValue !== undefined ? { properties: attributesValue } : {}),
              } : undefined}
              error={advancedError || undefined}
              note="Metadata and properties are separate Ordinals fields. This is what the review screen will carry into the inscription."
            />
            <section className="advanced-section"><div><h3>SAT & PARENT</h3></div><div className="advanced-grid">
              <ParentFields primary={parentId} onPrimary={setParentId} coParents={coParentIds} onCoParents={setCoParentIds} />
              <div className="choice-row full"><button type="button" className={sourceMode === "none" ? "active" : ""} onClick={() => setSourceMode("none")}>DEFAULT</button><button type="button" className={sourceMode === "sat" ? "active" : ""} onClick={() => setSourceMode("sat")}>SPECIAL SAT</button><button type="button" className={sourceMode === "reinscribe" ? "active" : ""} onClick={() => setSourceMode("reinscribe")}>REINSCRIBE</button></div>
              {sourceMode === "sat" ? <RareSatPicker {...rareSats.pickerProps} /> : null}
              {sourceMode === "reinscribe" ? <label className="field full"><span>INSCRIPTION ID</span><input value={reinscriptionId} onChange={(event) => setReinscriptionId(event.target.value)} placeholder={`${"0".repeat(64)}i0`} /></label> : null}
            </div></section>
            <section className={revealOpen ? "advanced-section danger-zone reveal-section open" : "advanced-section danger-zone reveal-section"}>
              <button className="reveal-toggle" type="button" aria-expanded={revealOpen} aria-controls="single-reveal-options" onClick={() => setRevealOpen((open) => !open)}>
                <span><strong>REVEAL</strong><small>{revealSettingCount ? `${revealSettingCount} ${revealSettingCount === 1 ? "SETTING" : "SETTINGS"} ACTIVE` : "ADVANCED INSCRIPTION SETTINGS"}</small></span>
                <ChevronDown className={revealOpen ? "rotate" : ""} />
              </button>
              {revealOpen ? <div className="reveal-options" id="single-reveal-options">
                <div className="toggle-grid">
                  {mode === "hive" ? <div className="check-row reveal-choice locked-choice"><CheckCircle2 /><span><strong>BROTLI REFERENCE</strong><small>The tiny Hive reference is compressed automatically.</small></span></div> : <label className="check-row reveal-choice"><input type="checkbox" checked={brotli} onChange={(event) => setBrotli(event.target.checked)} /><span><strong>{compressing ? "COMPRESSING…" : "BROTLI"}</strong><small>Compresses the file before inscription to reduce its on-chain size.</small></span></label>}
                  <label className="check-row reveal-choice"><input type="checkbox" checked={ephemeral} onChange={(event) => setEphemeral(event.target.checked)} /><span><strong>RECOVERY KEY</strong><small>Creates a recovery key for the reveal transaction. Keep it private.</small></span></label>
                  <label className="check-row reveal-choice"><input type="checkbox" checked={opReturn} onChange={(event) => setOpReturn(event.target.checked)} /><span><strong>OP_RETURN</strong><small>Adds a short public message to the transaction, outside the inscription.</small></span></label>
                  <label className={mode === "delegate" ? "check-row reveal-choice locked-choice" : "check-row reveal-choice"}><input type="checkbox" checked={burn} disabled={mode === "delegate"} onChange={(event) => setBurn(event.target.checked)} /><span><strong>BURN</strong><small>{mode === "delegate" ? "Delegates use Taproot destinations here. Use the dedicated Burn tool instead." : "Sends the inscription to an unspendable output. This is permanent."}</small></span></label>
                </div>
                <div className="advanced-grid engineering-fields">
                  <label className="field"><span>VANITY START</span><input maxLength={5} value={vanityStart} onChange={(event) => setVanityStart(event.target.value)} placeholder="a1" /><small>Tries to start the inscription ID with these hexadecimal characters. More characters take longer.</small></label>
                  <label className="field"><span>VANITY END</span><input maxLength={5} value={vanityEnd} onChange={(event) => setVanityEnd(event.target.value)} placeholder="ff" /><small>Tries to end the inscription ID with these hexadecimal characters. More characters take longer.</small></label>
                  {opReturn ? <label className="field full"><span>OP_RETURN MESSAGE</span><input value={opReturnData} onChange={(event) => setOpReturnData(event.target.value)} /><small>Plain text to store publicly in the Bitcoin transaction.</small></label> : null}
                  {burn ? <label className="field full"><span>BURN MESSAGE</span><textarea rows={3} value={burnMessage} onChange={(event) => setBurnMessage(event.target.value)} /><small>Optional text included in the unspendable burn output.</small></label> : null}
                </div>
              </div> : null}
            </section>
            {advancedError ? <Notice tone="error">{advancedError}</Notice> : null}
          </div> : null}
        </section>
        <SubmitPanel
          drafts={draft}
          feeRate={feeRate}
          onFeeRate={setFeeRate}
          resolveRevealOptions={resolveRevealOptions}
          title={file?.name ?? draft[0]?.name ?? "Single inscription"}
          displayCount={mode === "hive" && file ? 1 : undefined}
          prepareDrafts={mode === "hive" && file ? prepareSingleHiveDrafts : undefined}
          preparationProgress={mode === "hive" ? hiveProgress : undefined}
          disabledReason={mode === "hive" && !config?.hiveMediaEnabled ? "Hive media is not configured yet." : mode === "hive" && !file ? "Choose Hive media first." : mode !== "hive" && !hasSource ? "Choose a file, enter text, or provide a delegate ID." : delegateError || (compressing ? "Wait for Brotli compression to finish." : advancedError || undefined)}
        />
      </div>
    </main>
  );
}

type GalleryCoverMode = "auto" | "inscription" | "upload" | "text";

function GalleryPage() {
  const { fees } = useAppState();
  const rareSats = useWalletRareSatSelection(1);
  const specificSat = useSpecificSatSelection();
  const [galleryInput, setGalleryInput] = useState("");
  const [showExample, setShowExample] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [artist, setArtist] = useState("");
  const [website, setWebsite] = useState("");
  const [metadata, setMetadata] = useState("");
  const [traitRows, setTraitRows] = useState<TraitRowValue[]>(() => [newTraitRow()]);
  const [coverMode, setCoverMode] = useState<GalleryCoverMode>("auto");
  const [coverInscriptionId, setCoverInscriptionId] = useState("");
  const coverUpload = useGalleryFile();
  const coverFile = coverUpload.selection?.file;
  const coverBytes = coverUpload.selection?.body;
  const coverMimeType = coverUpload.selection?.mimeType;
  const [coverText, setCoverText] = useState("");
  const [parentId, setParentId] = useState("");
  const [coParentIds, setCoParentIds] = useState<string[]>([]);
  const [recipient, setRecipient] = useState("");
  const [satMode, setSatMode] = useState<"normal" | "rare" | "specific">("normal");
  const [feeRate, setFeeRate] = useState(12);
  const [readError, setReadError] = useState("");
  const [coverChecking, setCoverChecking] = useState(false);
  const [coverResolutionError, setCoverResolutionError] = useState("");
  const [coverResolution, setCoverResolution] = useState<Awaited<ReturnType<typeof resolveImageInscription>> | null>(null);
  const [resolvedCoverBytes, setResolvedCoverBytes] = useState<Uint8Array>();
  const jsonInputRef = useRef<HTMLInputElement>(null);
  const parsed = useMemo(() => parseGalleryInput(galleryInput), [galleryInput]);
  const coverPreviewUrl = useMemo(() => coverFile?.type.startsWith("image/") ? URL.createObjectURL(coverFile) : "", [coverFile]);
  const coverTextBytes = useMemo(() => new TextEncoder().encode(coverText), [coverText]);

  useEffect(() => { if (fees) setFeeRate(fees.halfHourFee); }, [fees]);
  useEffect(() => () => { if (coverPreviewUrl) URL.revokeObjectURL(coverPreviewUrl); }, [coverPreviewUrl]);

  const parsedAttributes = useMemo(() => {
    try {
      const custom = attributesFromRows(traitRows, title);
      const traits: Record<string, TraitValue> = { ...(custom?.traits ?? {}) };
      if (description.trim()) traits.description = description.trim();
      if (artist.trim()) traits.artist = artist.trim();
      if (website.trim()) traits.website = website.trim();
      return {
        attributes: custom?.title || Object.keys(traits).length ? { title: custom?.title, traits: Object.keys(traits).length ? traits : undefined } : undefined,
        error: "",
      };
    } catch (caught) {
      return { attributes: undefined, error: caught instanceof Error ? caught.message : "Gallery details are invalid." };
    }
  }, [artist, description, title, traitRows, website]);

  const parsedMetadata = useMemo(() => {
    try {
      return { value: parseMetadata(metadata), error: "" };
    } catch (caught) {
      return { value: undefined, error: caught instanceof Error ? caught.message : "Gallery metadata is invalid." };
    }
  }, [metadata]);

  const propertiesBytes = useMemo(() => {
    if (!parsed.items.length) return 0;
    return propertyTags(parsedAttributes.attributes, parsed.items)?.reduce((total, [, value]) => total + value.length, 0) ?? 0;
  }, [parsed.items, parsedAttributes.attributes]);
  const galleryBytes = propertiesBytes
    + (coverMode === "upload" ? coverBytes?.length ?? 0 : 0)
    + (coverMode === "text" ? coverTextBytes.length : 0)
    + (coverMode === "auto" || coverMode === "inscription" ? resolvedCoverBytes?.length ?? 0 : 0);
  const galleryPercent = Math.min(100, Math.round(galleryBytes / MAX_INSCRIPTION_FILE_BYTES * 100));
  const selectedCoverId = coverMode === "auto"
    ? parsed.items[0]?.inscriptionId ?? ""
    : coverMode === "inscription" ? coverInscriptionId.trim() : "";
  const selectedCoverIsValid = INSCRIPTION_ID_PATTERN.test(selectedCoverId);
  const resolvedCoverId = coverResolution?.requestedId.toLowerCase() === selectedCoverId.toLowerCase()
    ? coverResolution.contentId
    : "";
  const coverError = coverMode === "upload"
    ? coverFile && coverBytes?.length ? "" : "Choose a cover file."
    : coverMode === "text"
      ? coverTextBytes.length ? "" : "Enter the text to inscribe."
      : selectedCoverIsValid ? coverResolutionError : "Choose a valid cover inscription ID.";
  const coverPending = (coverMode === "upload" && coverUpload.pending) || (coverMode === "auto" || coverMode === "inscription") && selectedCoverIsValid && (coverChecking || !resolvedCoverId || !resolvedCoverBytes?.length);
  const sizeError = galleryBytes > MAX_INSCRIPTION_FILE_BYTES ? "This gallery is larger than 400 KB. Remove items or choose a smaller gallery cover." : "";
  const galleryError = (coverMode === "upload" ? coverUpload.error : "") || readError || parsed.error || parsedAttributes.error || parsedMetadata.error || sizeError;

  useEffect(() => {
    if (coverMode === "upload" || coverMode === "text" || !selectedCoverIsValid) {
      setCoverChecking(false);
      setCoverResolutionError("");
      setCoverResolution(null);
      setResolvedCoverBytes(undefined);
      return;
    }
    let cancelled = false;
    setCoverChecking(true);
    setCoverResolutionError("");
    setCoverResolution(null);
    setResolvedCoverBytes(undefined);
    void resolveImageInscription(selectedCoverId, true)
      .then(async (resolved) => {
        const content = await fetchOrdinalGalleryContent(resolved.contentId);
        if (!cancelled) {
          setCoverResolution({ ...resolved, contentType: content.mimeType, contentLength: content.body.length });
          setResolvedCoverBytes(content.body);
        }
      })
      .catch((caught) => { if (!cancelled) setCoverResolutionError(caught instanceof Error ? caught.message : "Could not verify this gallery cover"); })
      .finally(() => { if (!cancelled) setCoverChecking(false); });
    return () => { cancelled = true; };
  }, [coverMode, selectedCoverId, selectedCoverIsValid]);

  const galleryDataPreview = useMemo(() => {
    if (parsedMetadata.value === undefined && parsedAttributes.attributes === undefined && !parsed.items.length) return undefined;
    const visibleItems = parsed.items.slice(0, 12).map((item) => ({
      id: item.inscriptionId,
      ...(item.attributes?.title ? { title: item.attributes.title } : {}),
      ...(item.attributes?.traits ? { traits: item.attributes.traits } : {}),
    }));
    return {
      ...(parsedMetadata.value !== undefined ? { metadata: parsedMetadata.value } : {}),
      properties: {
        ...(parsedAttributes.attributes ? { attributes: parsedAttributes.attributes } : {}),
        gallery: {
          count: parsed.items.length,
          items: visibleItems,
          ...(parsed.items.length > visibleItems.length ? { remaining: parsed.items.length - visibleItems.length } : {}),
        },
      },
    };
  }, [parsed.items, parsedAttributes.attributes, parsedMetadata.value]);

  const drafts = useMemo<InscriptionDraft[]>(() => {
    if (!parsed.items.length || galleryError || coverError || coverPending) return [];
    const upload = coverMode === "upload";
    const textCover = coverMode === "text";
    const copiedCover = !upload && !textCover;
    return [{
      id: "gallery",
      name: title.trim() || "gallery",
      mimeType: upload ? coverMimeType : textCover ? "text/plain;charset=utf-8" : coverResolution?.contentType,
      body: upload ? coverBytes : textCover ? coverTextBytes : resolvedCoverBytes,
      previewBody: upload ? coverBytes : copiedCover ? resolvedCoverBytes : undefined,
      recipient: recipient.trim() || undefined,
      metadata: parsedMetadata.value,
      attributes: parsedAttributes.attributes,
      gallery: parsed.items,
      postage: DEFAULT_POSTAGE,
    }];
  }, [coverBytes, coverError, coverMimeType, coverMode, coverPending, coverResolution?.contentType, coverTextBytes, galleryError, parsed.items, parsedAttributes.attributes, parsedMetadata.value, recipient, resolvedCoverBytes, title]);

  const chooseCover = (files: File[]) => {
    setReadError("");
    coverUpload.chooseFiles(files);
  };

  const loadJson = async (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0];
    if (!next) return;
    try {
      setGalleryInput(await next.text());
      setReadError("");
    } catch {
      setReadError("That JSON file could not be read.");
    } finally {
      event.target.value = "";
    }
  };

  const selectedRareSat = rareSats.selectedSources[0];
  const resolveGalleryOptions = async (connected: ConnectedWallet): Promise<RevealOptions> => {
    if (satMode === "rare" && !selectedRareSat) throw new Error("Choose a rare sat for this gallery.");
    if (satMode === "specific" && !specificSat.source) throw new Error("Resolve and confirm the exact specific sat before continuing.");
    const [parents, source] = await Promise.all([
      resolveParentSelection(connected, parentId, coParentIds),
      satMode === "rare" && selectedRareSat
        ? resolveWalletSatSources(connected.ordinals.address, [selectedRareSat]).then(([resolved]) => resolved)
        : satMode === "specific" ? specificSat.source : undefined,
    ]);
    return { ...parents, source };
  };

  return (
    <main className="page-shell gallery-page">
      <PageIntro eyebrow="INSCRIBE // GALLERY" title="GALLERY" accent="" description="GROUP EXISTING INSCRIPTIONS ON-CHAIN" />
      <div className="workspace-grid gallery-workspace">
        <section className="maker-card gallery-maker">
          <div className="gallery-builder-heading"><span><LayoutGrid /></span><div><p className="eyebrow">ON-CHAIN GALLERY</p><h2>BUILD THE LIST</h2><small>The items stay in their current wallets. You only inscribe a gallery that points to them.</small></div></div>

          <section className="gallery-step">
            <header><span>01</span><div><h3>INSCRIPTION IDS</h3><small>PASTE ONE PER LINE, OR USE JSON TO ADD NAMES AND TRAITS</small></div><div className="gallery-step-actions"><button className="secondary-button compact" type="button" onClick={() => setShowExample((open) => !open)}>{showExample ? "HIDE EXAMPLE" : "JSON EXAMPLE"}</button><button className="secondary-button compact" type="button" onClick={() => jsonInputRef.current?.click()}><FileJson /> LOAD JSON</button><input ref={jsonInputRef} type="file" hidden accept=".json,application/json" onChange={(event) => void loadJson(event)} /></div></header>
            {showExample ? <div className="gallery-json-example"><pre>{GALLERY_INPUT_EXAMPLE}</pre><CopyButton value={GALLERY_INPUT_EXAMPLE} label="Copy gallery JSON example" /></div> : null}
            <textarea className="gallery-id-input" rows={9} spellCheck={false} value={galleryInput} onChange={(event) => { setReadError(""); setGalleryInput(event.target.value); }} placeholder={`PASTE INSCRIPTION IDS\n${"0".repeat(64)}i0\n${"1".repeat(64)}i0\n\nOR PASTE JSON WITH NAMES + TRAITS`} />
            <div className="gallery-input-status">
              <span className="valid">{numberFormatter.format(parsed.items.length)} VALID</span>
              {parsed.withMetadata ? <span>{numberFormatter.format(parsed.withMetadata)} WITH ITEM ATTRIBUTES</span> : null}
              {parsed.invalidCount ? <span className="invalid">{numberFormatter.format(parsed.invalidCount)} INVALID</span> : null}
              {parsed.duplicateCount ? <span>{numberFormatter.format(parsed.duplicateCount)} DUPLICATES REMOVED</span> : null}
            </div>
            {parsed.error ? <Notice tone="error">{parsed.error}</Notice> : null}
            {parsed.items.length ? <GalleryItemsPreview items={parsed.items} limit={13} /> : null}
          </section>

          {parsed.items.length ? <>
            <section className="gallery-step">
              <header><span>02</span><div><h3>GALLERY DATA</h3><small>PROPERTIES FOR DISPLAY, PLUS OPTIONAL FREE-FORM METADATA</small></div></header>
              <div className="advanced-grid gallery-info-fields">
                <label className="field full"><span>TITLE</span><input value={title} onChange={(event) => setTitle(event.target.value)} placeholder="GALLERY TITLE" /></label>
                <label className="field full"><span>DESCRIPTION</span><textarea rows={3} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="WHAT IS THIS GALLERY?" /></label>
                <label className="field"><span>ARTIST</span><input value={artist} onChange={(event) => setArtist(event.target.value)} placeholder="OPTIONAL" /></label>
                <label className="field"><span>WEBSITE</span><input value={website} onChange={(event) => setWebsite(event.target.value)} placeholder="HTTPS://" /></label>
                <label className="field full"><span>ON-CHAIN METADATA <small>JSON</small></span><textarea rows={5} value={metadata} onChange={(event) => setMetadata(event.target.value)} placeholder={'{"collection":"21 ETERNAL","year":2026}'} /><small>Optional. This is the Ordinals metadata field. Title, artist, description and traits remain gallery properties.</small></label>
                <div className="trait-editor full">
                  <span className="trait-editor-label">EXTRA TRAITS</span>
                  <div className="trait-list">
                    {traitRows.map((row, index) => (
                      <div className="trait-row" key={row.id}>
                        <input aria-label={`Gallery trait ${index + 1} name`} value={row.name} onChange={(event) => setTraitRows((current) => current.map((candidate) => candidate.id === row.id ? { ...candidate, name: event.target.value } : candidate))} placeholder="TRAIT NAME" />
                        {row.type === "boolean" ? <select aria-label={`Gallery trait ${index + 1} value`} value={row.value || "true"} onChange={(event) => setTraitRows((current) => current.map((candidate) => candidate.id === row.id ? { ...candidate, value: event.target.value } : candidate))}><option value="true">TRUE</option><option value="false">FALSE</option></select> : <input aria-label={`Gallery trait ${index + 1} value`} disabled={row.type === "null"} inputMode={row.type === "number" ? "numeric" : undefined} value={row.value} onChange={(event) => setTraitRows((current) => current.map((candidate) => candidate.id === row.id ? { ...candidate, value: event.target.value } : candidate))} placeholder={row.type === "null" ? "NULL" : "VALUE"} />}
                        <select aria-label={`Gallery trait ${index + 1} type`} value={row.type} onChange={(event) => { const type = event.target.value as TraitInputType; setTraitRows((current) => current.map((candidate) => candidate.id === row.id ? { ...candidate, type, value: type === "boolean" ? "true" : type === "null" ? "" : candidate.value } : candidate)); }}><option value="text">TEXT</option><option value="number">NUMBER</option><option value="boolean">BOOLEAN</option><option value="null">NULL</option></select>
                        <button className="remove-trait-button" type="button" aria-label={`Remove gallery trait ${index + 1}`} onClick={() => setTraitRows((current) => current.filter((candidate) => candidate.id !== row.id))}><X /></button>
                      </div>
                    ))}
                  </div>
                  <button className="add-trait-button" type="button" onClick={() => setTraitRows((current) => [...current, newTraitRow()])}><Plus /> ADD TRAIT</button>
                </div>
              </div>
              <DataPreview
                label="GALLERY DATA PREVIEW"
                value={galleryDataPreview}
                error={parsedMetadata.error || parsedAttributes.error || undefined}
                note={parsed.items.length > 12 ? `Showing the first 12 of ${numberFormatter.format(parsed.items.length)} gallery items. The full list will be inscribed.` : "Metadata and properties are encoded as separate Ordinals fields."}
              />
            </section>

            <section className="gallery-step">
              <header><span>03</span><div><h3>GALLERY COVER</h3><small>USE AN IMAGE, HTML FILE OR TEXT</small></div></header>
              <div className="gallery-cover-choices">
                <label className={coverMode === "auto" ? "active" : ""}><input type="radio" name="gallery-cover" checked={coverMode === "auto"} onChange={() => setCoverMode("auto")} /><span><strong>FIRST GALLERY ITEM</strong><small>Uses its underlying image or HTML as the gallery cover.</small></span></label>
                <label className={coverMode === "inscription" ? "active" : ""}><input type="radio" name="gallery-cover" checked={coverMode === "inscription"} onChange={() => setCoverMode("inscription")} /><span><strong>IMAGE / HTML INSCRIPTION ID</strong><small>Uses that on-chain image or HTML and follows delegate links.</small></span></label>
                <label className={coverMode === "upload" ? "active" : ""}><input type="radio" name="gallery-cover" checked={coverMode === "upload"} onChange={() => setCoverMode("upload")} /><span><strong>UPLOAD IMAGE OR HTML</strong><small>Inscribes the uploaded file as the gallery cover.</small></span></label>
                <label className={coverMode === "text" ? "active" : ""}><input type="radio" name="gallery-cover" checked={coverMode === "text"} onChange={() => setCoverMode("text")} /><span><strong>INSCRIBE TEXT</strong><small>A simple, low-size text inscription for the gallery.</small></span></label>
              </div>
              {coverMode === "inscription" ? <div className="gallery-cover-picker"><label className="field"><span>GALLERY IMAGE / HTML INSCRIPTION ID</span><input value={coverInscriptionId} onChange={(event) => setCoverInscriptionId(event.target.value)} placeholder={`${"0".repeat(64)}i0`} /></label><div className="gallery-cover-pick-grid">{parsed.items.slice(0, 24).map((item, index) => <button className={coverInscriptionId === item.inscriptionId ? "selected" : ""} type="button" key={item.inscriptionId} title={item.attributes?.title ?? item.inscriptionId} onClick={() => setCoverInscriptionId(item.inscriptionId)}><GalleryPreviewImage item={item} /><small>{String(index + 1).padStart(2, "0")}</small></button>)}</div></div> : null}
              {coverMode === "upload" ? <div className="gallery-cover-upload"><DropZone compact onFiles={chooseCover} limitLabel="IMAGE / HTML · MAX 400 KB" accept={GALLERY_FILE_ACCEPT} />{coverFile ? <div className="gallery-cover-file">{coverBytes && coverMimeType && isHtmlContentType(coverMimeType) ? <LocalInscriptionDocument body={coverBytes} mimeType={coverMimeType} title="Gallery HTML preview" /> : coverPreviewUrl ? <img src={coverPreviewUrl} alt="Gallery image preview" /> : <FileArchive />}<span><strong>{coverFile.name}</strong><small>{coverMimeType} · {bytes(coverFile.size)}</small></span><button className="icon-button danger" type="button" aria-label="Remove gallery cover" onClick={coverUpload.clear}><Trash2 /></button></div> : null}</div> : null}
              {coverMode === "text" ? <div className="gallery-cover-text"><label className="field"><span>TEXT TO INSCRIBE</span><textarea rows={6} value={coverText} onChange={(event) => setCoverText(event.target.value)} placeholder="TYPE OR PASTE THE GALLERY TEXT…" /><small>{bytes(coverTextBytes.length)} · TEXT/PLAIN</small></label></div> : null}
              {(coverMode === "auto" || coverMode === "inscription") && selectedCoverIsValid ? (
                <div className={coverResolutionError ? "gallery-cover-resolution invalid" : "gallery-cover-resolution"}>
                  {coverChecking ? <><LoaderCircle className="spin" /><span><strong>CHECKING COVER</strong><small>FOLLOWING DELEGATE LINKS…</small></span></> : coverResolutionError ? <><X /><span><strong>COVER NOT USABLE</strong><small>{coverResolutionError}</small></span></> : coverResolution ? <><GalleryPreviewImage item={{ inscriptionId: coverResolution.contentId }} /><span><strong>COVER READY · {coverResolution.contentType.toUpperCase()}</strong><code>{coverResolution.contentId}</code><small>{bytes(coverResolution.contentLength)}{coverResolution.delegateDepth ? ` · FOLLOWED ${coverResolution.delegateDepth} ${coverResolution.delegateDepth === 1 ? "DELEGATE" : "DELEGATES"}` : " · DIRECT CONTENT"}</small></span></> : null}
                </div>
              ) : null}
            </section>

            <section className="gallery-step">
              <header><span>04</span><div><h3>DELIVERY</h3><small>OPTIONAL PARENT AND DESTINATION</small></div></header>
              <div className="advanced-grid gallery-info-fields">
                <ParentFields primary={parentId} onPrimary={setParentId} coParents={coParentIds} onCoParents={setCoParentIds} />
                <label className="field full"><span>SEND GALLERY TO</span><input value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="LEAVE BLANK FOR YOUR ORDINALS WALLET" /></label>
              </div>
            </section>

            <section className="gallery-step gallery-sat-step">
              <header><span>05</span><div><h3>INSCRIPTION SAT</h3><small>USE A NORMAL SAT OR PLACE THE GALLERY ON A SAT FROM YOUR TAPROOT WALLET</small></div></header>
              <div className="gallery-cover-choices gallery-sat-choices">
                <label className={satMode === "normal" ? "active" : ""}>
                  <input type="radio" name="gallery-sat" checked={satMode === "normal"} onChange={() => { setSatMode("normal"); rareSats.clear(); specificSat.clear(); }} />
                  <span><strong>NORMAL SAT</strong><small>Default. The gallery receives standard postage.</small></span>
                </label>
                <label className={satMode === "rare" ? "active" : ""}>
                  <input type="radio" name="gallery-sat" checked={satMode === "rare"} onChange={() => { setSatMode("rare"); specificSat.clear(); }} />
                  <span><strong>PICK RARE SAT</strong><small>Choose one clean first-position sat from your ordinals wallet.</small></span>
                </label>
                <label className={satMode === "specific" ? "active" : ""}>
                  <input type="radio" name="gallery-sat" checked={satMode === "specific"} onChange={() => { setSatMode("specific"); rareSats.clear(); specificSat.clear(); }} />
                  <span><strong>SPECIFIC SAT</strong><small>Enter a decimal sat number, then resolve and confirm its live location.</small></span>
                </label>
              </div>
              {satMode === "rare" ? <>
                <RareSatPicker {...rareSats.pickerProps} />
                <p className="gallery-sat-note">The selected sat stays in the gallery output. The price is paid from your SegWit payment wallet.</p>
              </> : null}
              {satMode === "specific" ? <>
                <SpecificSatPicker {...specificSat.pickerProps} />
                <p className="gallery-sat-note">Only the verified Taproot ordinals wallet supplies this sat. The price is paid from your SegWit payment wallet.</p>
              </> : null}
            </section>

            <section className="gallery-size-card">
              <div><span>GALLERY SIZE · {numberFormatter.format(parsed.items.length)} ITEMS</span><strong>{bytes(galleryBytes)} / 400 KB</strong></div>
              <span><i style={{ width: `${galleryPercent}%` }} /></span>
              {sizeError ? <Notice tone="error">{sizeError}</Notice> : null}
            </section>
          </> : null}
          {galleryError && !parsed.error && !sizeError ? <Notice tone="error">{galleryError}</Notice> : null}
        </section>
        <SubmitPanel drafts={drafts} feeRate={feeRate} onFeeRate={setFeeRate} resolveRevealOptions={resolveGalleryOptions} planDependency={`${satMode}:${selectedRareSat?.outpoint ?? ""}:${specificSat.source?.satpoint ?? ""}:${specificSat.source?.parentRelation === true}`} title={title.trim() || "Gallery inscription"} disabledReason={!parsed.items.length ? "Add at least one valid inscription ID." : coverPending ? "Checking the gallery cover." : satMode === "rare" && !selectedRareSat ? "Choose a rare sat for this gallery." : satMode === "specific" && !specificSat.confirmed ? "Resolve and confirm the exact specific sat." : galleryError || coverError || undefined} returnPath="/gallery" />
      </div>
    </main>
  );
}

interface BatchFile {
  id: string;
  file: File;
  bytes?: Uint8Array;
  compressedBytes?: Uint8Array;
  hiveMedia?: PreparedHiveMedia;
  hiveProgress?: number;
  hiveUploading?: boolean;
  hiveError?: string;
  recipient: string;
}

interface BatchMetadataEditorValue {
  metadata: string;
  title: string;
  traits: TraitRowValue[];
}

function newBatchMetadataEditor(): BatchMetadataEditorValue {
  return { metadata: "", title: "", traits: [newTraitRow()] };
}

function cloneBatchMetadataEditor(value: BatchMetadataEditorValue): BatchMetadataEditorValue {
  return {
    metadata: value.metadata,
    title: value.title,
    traits: value.traits.map((trait) => ({ ...trait, id: crypto.randomUUID() })),
  };
}

function batchMetadataEditorHasData(value?: BatchMetadataEditorValue): boolean {
  return Boolean(value && (
    value.metadata.trim()
    || value.title.trim()
    || value.traits.some((trait) => trait.name.trim() || trait.value.trim() || trait.type === "null")
  ));
}

function BatchPage() {
  const { fees, config, authorization, authorizeWallet } = useAppState();
  const [mode, setMode] = useState<"files" | "hive" | "delegates">("files");
  const [items, setItems] = useState<BatchFile[]>([]);
  const [metadataEditors, setMetadataEditors] = useState<Record<string, BatchMetadataEditorValue>>({});
  const [activeMetadataItemId, setActiveMetadataItemId] = useState("");
  const [metadataCopied, setMetadataCopied] = useState(false);
  const [delegateYaml, setDelegateYaml] = useState("");
  const [destinationCsvImport, setDestinationCsvImport] = useState<{ mappingKey: string; addresses: string[] }>({ mappingKey: "", addresses: [] });
  const [bulkMetadata, setBulkMetadata] = useState("");
  const [itemYaml, setItemYaml] = useState("");
  const [parentId, setParentId] = useState("");
  const [coParentIds, setCoParentIds] = useState<string[]>([]);
  const [postage, setPostage] = useState(DEFAULT_POSTAGE);
  const [brotli, setBrotli] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [feeRate, setFeeRate] = useState(12);
  const [error, setError] = useState("");
  const [hiveBatchUploading, setHiveBatchUploading] = useState(false);
  const destinationMappingKey = mode === "delegates"
    ? `delegates:${delegateYaml}`
    : `${mode}:${items.map((item) => item.id).join(",")}`;
  const destinationCsvRecipients = destinationCsvImport.mappingKey === destinationMappingKey
    ? destinationCsvImport.addresses
    : [];
  const clearDestinationCsvImport = () => setDestinationCsvImport({ mappingKey: "", addresses: [] });
  useEffect(() => { if (fees) setFeeRate(fees.halfHourFee); }, [fees]);

  const addFiles = (files: File[]) => {
    const hadImportedDestinations = destinationCsvRecipients.length > 0;
    const availableSlots = Math.max(0, MAX_BATCH_ITEMS - items.length);
    const candidates = files.slice(0, availableSlots);
    const messages: string[] = [];
    const accepted = mode === "hive" ? candidates.filter((file) => {
      const validationError = validateHiveImageFile(file);
      if (validationError && !messages.includes(validationError)) messages.push(validationError);
      return !validationError;
    }) : (() => {
      const selection = filterBatchFilesForCompression(candidates);
      if (selection.oversizedCount) messages.push("Files must be 400 KB or smaller.");
      return selection.accepted;
    })();
    if (files.length > availableSlots) messages.push(`A batch can contain at most ${MAX_BATCH_ITEMS} files.`);
    setError(messages.join(" "));
    const additions = accepted.map((file) => ({ id: crypto.randomUUID(), file, recipient: "" }));
    if (additions.length && hadImportedDestinations) clearDestinationCsvImport();
    setItems((current) => [
      ...(additions.length && hadImportedDestinations ? current.map((item) => ({ ...item, recipient: "" })) : current),
      ...additions,
    ].slice(0, MAX_BATCH_ITEMS));
    setMetadataEditors((current) => {
      const next = { ...current };
      additions.forEach((item) => { next[item.id] = newBatchMetadataEditor(); });
      return next;
    });
    setActiveMetadataItemId((current) => current || additions[0]?.id || "");
    if (mode !== "hive") additions.forEach((item) => void item.file.arrayBuffer().then(async (buffer) => {
      const bytesValue = new Uint8Array(buffer);
      const compressedBytes = brotli ? await brotliCompress(bytesValue) : undefined;
      setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, bytes: bytesValue, compressedBytes } : candidate));
    }));
  };

  const uploadBatchToHive = async (): Promise<Map<string, PreparedHiveMedia>> => {
    const preparedById = new Map(items.flatMap((item) => item.hiveMedia ? [[item.id, item.hiveMedia] as const] : []));
    const pending = items.filter((item) => !item.hiveMedia);
    if (!pending.length) return preparedById;
    const clientInscriptionId = config?.hiveMediaClientInscriptionId;
    if (!config?.hiveMediaEnabled || !clientInscriptionId) {
      throw new Error("Hive media is not configured yet.");
    }
    setError("");
    setHiveBatchUploading(true);
    try {
      const session = authorization ?? await authorizeWallet();
      if (!session) throw new Error("Connect and verify your wallet to upload to Hive");
      const permits = await requestHiveUploadAuthorizations(session, pending.map((item) => item.file));
      setItems((current) => current.map((item) => pending.some((candidate) => candidate.id === item.id) ? { ...item, hiveUploading: true, hiveError: "", hiveProgress: 0 } : item));
      let cursor = 0;
      const failures: string[] = [];
      const worker = async () => {
        while (cursor < pending.length) {
          const index = cursor++;
          const item = pending[index];
          const permit = permits[index];
          if (!item) continue;
          if (!permit) {
            const message = "Hive did not authorize the upload";
            failures.push(`${item.file.name}: ${message}`);
            setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, hiveUploading: false, hiveError: message } : candidate));
            continue;
          }
          try {
            const prepared = await prepareHiveMedia(item.file, permit, session, clientInscriptionId, (progress) => {
              setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, hiveProgress: progress } : candidate));
            });
            preparedById.set(item.id, prepared);
            setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, hiveMedia: prepared, hiveUploading: false, hiveProgress: 100, hiveError: "" } : candidate));
          } catch (caught) {
            const message = caught instanceof Error ? caught.message : "Hive upload failed";
            failures.push(`${item.file.name}: ${message}`);
            setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, hiveUploading: false, hiveError: message } : candidate));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, pending.length) }, () => worker()));
      if (failures.length) throw new Error(`${failures.length} Hive ${failures.length === 1 ? "upload needs" : "uploads need"} retrying.`);
      return preparedById;
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "Hive uploads failed";
      setError(message);
      throw caught instanceof Error ? caught : new Error(message);
    } finally {
      setHiveBatchUploading(false);
    }
  };
  const toggleBrotli = (enabled: boolean) => {
    setBrotli(enabled);
    if (!enabled) { setItems((current) => current.map((item) => ({ ...item, compressedBytes: undefined }))); return; }
  };
  useEffect(() => {
    const missing = brotli ? items.filter((item) => item.bytes && !item.compressedBytes) : [];
    if (!missing.length) return;
    let cancelled = false;
    setCompressing(true);
    void Promise.all(missing.map(async (item) => ({ id: item.id, value: await brotliCompress(item.bytes ?? new Uint8Array()) })))
      .then((values) => {
        if (cancelled) return;
        const byId = new Map(values.map((entry) => [entry.id, entry.value]));
        setItems((current) => current.map((item) => byId.has(item.id) ? { ...item, compressedBytes: byId.get(item.id) } : item));
      })
      .catch(() => { if (!cancelled) setError("Brotli compression could not be prepared."); })
      .finally(() => { if (!cancelled) setCompressing(false); });
    return () => { cancelled = true; };
  }, [brotli, items]);
  const buildBatchDrafts = (hiveMediaById?: Map<string, PreparedHiveMedia>): InscriptionDraft[] => {
    const base: InscriptionDraft[] = mode === "delegates"
      ? (delegateYaml.trim() ? parseDelegateBatchYaml(delegateYaml) : [])
      : mode === "hive"
        ? items.flatMap((item) => {
          const prepared = hiveMediaById?.get(item.id) ?? item.hiveMedia;
          return prepared ? [{
            id: item.id, name: item.file.name, mimeType: "text/html;charset=utf-8",
            body: prepared.body, contentEncoding: "br" as const,
            previewFile: item.file.type.startsWith("image/") ? item.file : undefined,
            previewMimeType: item.file.type || "application/octet-stream",
            sourceLabel: `HIVE · ${item.file.type || "MEDIA"}`, sourceSize: item.file.size,
            recipient: item.recipient.trim() || undefined, postage,
          }] : [];
        })
        : items.filter((item) => item.bytes).map((item) => ({
          id: item.id, name: item.file.name, mimeType: item.file.type || "application/octet-stream",
          body: brotli ? item.compressedBytes : item.bytes, contentEncoding: brotli ? "br" as const : undefined,
          previewBody: item.file.type.startsWith("image/") ? item.bytes : undefined,
          recipient: item.recipient.trim() || undefined, postage,
        }));
    const withBulkMetadata = mode !== "delegates" && bulkMetadata.trim() ? applyBulkMetadataPaste(base, bulkMetadata) : base;
    const withYaml = itemYaml.trim() ? applyBatchYaml(withBulkMetadata, itemYaml) : withBulkMetadata;
    const withCsvDestinations = destinationCsvRecipients.length && withYaml.length === destinationCsvRecipients.length
      ? applyDestinationAddresses(withYaml, destinationCsvRecipients)
      : withYaml;
    return withCsvDestinations.map((draft) => {
      const editor = metadataEditors[draft.id];
      if (!editor || !batchMetadataEditorHasData(editor)) return draft;
      try {
        const hasMetadata = Boolean(editor.metadata.trim());
        const builtAttributes = attributesFromRows(editor.traits, editor.title);
        return {
          ...draft,
          metadata: hasMetadata ? parseMetadata(editor.metadata) : draft.metadata,
          attributes: builtAttributes ?? draft.attributes,
        };
      } catch (caught) {
        throw new Error(`${draft.name}: ${caught instanceof Error ? caught.message : "Metadata is invalid"}`);
      }
    });
  };
  let drafts: InscriptionDraft[] = []; let yamlError = "";
  try { drafts = buildBatchDrafts(); }
  catch (caught) { yamlError = caught instanceof Error ? caught.message : "Batch YAML is invalid"; }
  const prepareBatchHiveDrafts = async (): Promise<InscriptionDraft[]> => {
    const preparedById = await uploadBatchToHive();
    const preparedDrafts = buildBatchDrafts(preparedById);
    if (preparedDrafts.length !== items.length) throw new Error("Every Hive upload must finish before review");
    return preparedDrafts;
  };
  const hiveBatchProgress = items.length
    ? Math.round(items.reduce((total, item) => total + (item.hiveMedia ? 100 : item.hiveProgress ?? 0), 0) / items.length)
    : 0;
  const rareSatSelectionLimit = batchRareSatSelectionLimit(mode, items.length, drafts.length);
  const rareSats = useWalletRareSatSelection(rareSatSelectionLimit);
  const selectedWalletSats = rareSats.selectedSources;
  const resolveRevealOptions = async (connected: ConnectedWallet): Promise<RevealOptions> => {
    const [parents, sources] = await Promise.all([
      resolveParentSelection(connected, parentId, coParentIds),
      selectedWalletSats.length ? resolveWalletSatSources(connected.ordinals.address, selectedWalletSats) : undefined,
    ]);
    return { ...parents, sources };
  };
  const rawTotalBytes = items.reduce((total, item) => total + item.file.size, 0);
  const compressedPayloadReady = mode !== "files" || !brotli || (items.every((item) => item.bytes && item.compressedBytes) && !compressing);
  const compressedTotalBytes = items.reduce((total, item) => total + (item.compressedBytes?.length ?? 0), 0);
  const totalBytes = mode === "files" && brotli && compressedPayloadReady ? compressedTotalBytes : rawTotalBytes;
  const batchSizeError = mode === "files" && compressedPayloadReady ? batchPayloadLimitMessage(totalBytes, brotli) : "";
  const activeMetadataIndex = items.findIndex((item) => item.id === activeMetadataItemId);
  const activeMetadataItem = activeMetadataIndex >= 0 ? items[activeMetadataIndex] : items[0];
  const activeMetadataEditor = activeMetadataItem ? metadataEditors[activeMetadataItem.id] : undefined;
  const updateActiveMetadataEditor = (update: (current: BatchMetadataEditorValue) => BatchMetadataEditorValue) => {
    if (!activeMetadataItem) return;
    setMetadataEditors((current) => ({
      ...current,
      [activeMetadataItem.id]: update(current[activeMetadataItem.id] ?? newBatchMetadataEditor()),
    }));
    setMetadataCopied(false);
  };
  const copyMetadataToOthers = () => {
    if (!activeMetadataItem || !activeMetadataEditor) return;
    setMetadataEditors((current) => {
      const next = { ...current };
      items.forEach((item) => {
        if (item.id !== activeMetadataItem.id) next[item.id] = cloneBatchMetadataEditor(activeMetadataEditor);
      });
      return next;
    });
    setMetadataCopied(true);
    window.setTimeout(() => setMetadataCopied(false), 1600);
  };
  const removeBatchItem = (id: string) => {
    const hadImportedDestinations = destinationCsvRecipients.length > 0;
    clearDestinationCsvImport();
    const remaining = items
      .filter((item) => item.id !== id)
      .map((item) => hadImportedDestinations ? { ...item, recipient: "" } : item);
    setItems(remaining);
    setMetadataEditors((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    if (activeMetadataItemId === id) setActiveMetadataItemId(remaining[0]?.id ?? "");
  };

  let activeMetadataPreview: unknown;
  let activeMetadataPreviewError = "";
  if (activeMetadataEditor && batchMetadataEditorHasData(activeMetadataEditor)) {
    try {
      const metadataValue = activeMetadataEditor.metadata.trim() ? parseMetadata(activeMetadataEditor.metadata) : undefined;
      const propertiesValue = attributesFromRows(activeMetadataEditor.traits, activeMetadataEditor.title);
      activeMetadataPreview = {
        ...(metadataValue !== undefined ? { metadata: metadataValue } : {}),
        ...(propertiesValue !== undefined ? { properties: propertiesValue } : {}),
      };
    } catch (caught) {
      activeMetadataPreviewError = caught instanceof Error ? caught.message : "Metadata is invalid";
    }
  }
  const bulkMetadataPreview = bulkMetadata.trim() && !yamlError
    ? drafts.filter((draft) => draft.metadata !== undefined).slice(0, 8).map((draft) => ({ file: draft.name, metadata: draft.metadata }))
    : undefined;
  return (
    <main className="page-shell batch-page">
      <PageIntro eyebrow="BATCH // 02" title="BATCH" accent="INSCRIBE." />
      <div className="workspace-grid batch-grid">
        <section className="maker-card">
          <div className="batch-toolbar"><div><p className="eyebrow">ITEMS</p><h2>{mode === "files" ? "FILES" : mode === "hive" ? "HIVE MEDIA" : "DELEGATE YAML"}</h2></div><span>{mode === "delegates" ? drafts.length : items.length} // {bytes(totalBytes)}</span></div>
          <div className="operation-tabs"><button type="button" disabled={hiveBatchUploading} className={mode === "files" ? "active" : ""} onClick={() => { setMode("files"); setItems([]); setMetadataEditors({}); setActiveMetadataItemId(""); clearDestinationCsvImport(); setBulkMetadata(""); setItemYaml(""); setError(""); }}><Upload /> Files</button><button type="button" disabled={hiveBatchUploading} className={mode === "hive" ? "active hive-tab" : "hive-tab"} onClick={() => { setMode("hive"); setItems([]); setMetadataEditors({}); setActiveMetadataItemId(""); clearDestinationCsvImport(); setBulkMetadata(""); setItemYaml(""); setBrotli(false); setError(""); }}><Radio /> Hive</button><button type="button" disabled={hiveBatchUploading} className={mode === "delegates" ? "active" : ""} onClick={() => { setMode("delegates"); setItems([]); setMetadataEditors({}); setActiveMetadataItemId(""); clearDestinationCsvImport(); setBulkMetadata(""); setItemYaml(""); setError(""); }}><FileJson /> Delegate YAML</button></div>
          {mode !== "delegates" && items.length ? (
            <div className="batch-table" role="table" aria-label="Batch inscription files">
              <div className="batch-row batch-head" role="row"><span>FILE</span><span>STATUS</span><span>DESTINATION</span><span>SIZE</span><span /></div>
              {items.map((item, index) => (
                <div className="batch-row" role="row" key={item.id}>
                  <span className="file-cell"><span className="file-index">{String(index + 1).padStart(2, "0")}</span><span><strong>{item.file.name}</strong><small>{item.file.type || "Binary file"}</small></span></span>
                  {mode === "hive" ? <span className={item.hiveMedia ? "valid" : item.hiveError ? "invalid" : "loading"}>{item.hiveMedia ? <><CheckCircle2 /> READY</> : item.hiveError ? <><X /> RETRY</> : item.hiveUploading ? <><LoaderCircle className="spin" /> {item.hiveProgress ?? 0}%</> : <>SELECTED</>}</span> : <span className={item.bytes ? "valid" : "loading"}>{item.bytes ? <><CheckCircle2 /> READY</> : <><LoaderCircle className="spin" /> READING</>}</span>}
                  <span className="recipient-cell"><input aria-label={`Destination for ${item.file.name}`} value={item.recipient} onChange={(event) => { clearDestinationCsvImport(); setItems((current) => current.map((candidate) => candidate.id === item.id ? { ...candidate, recipient: event.target.value } : candidate)); }} placeholder="WALLET" /><button className="apply-all-button" type="button" disabled={!item.recipient.trim() || items.length < 2} title="Use this address for every inscription" aria-label={`Use ${item.file.name}'s destination for every inscription`} onClick={() => { clearDestinationCsvImport(); setItems((current) => current.map((candidate) => ({ ...candidate, recipient: item.recipient.trim() }))); }}><Copy /> ALL</button></span>
                  <span>{brotli && item.compressedBytes ? `${bytes(item.compressedBytes.length)} BR` : bytes(item.file.size)}</span>
                  <button className="icon-button danger" type="button" aria-label={`Remove ${item.file.name}`} disabled={item.hiveUploading} onClick={() => removeBatchItem(item.id)}><Trash2 /></button>
                </div>
              ))}
            </div>
          ) : mode !== "delegates" ? <div className="batch-empty"><Layers3 /><h3>NO FILES</h3></div> : null}
          {mode !== "delegates" ? <><DropZone onFiles={addFiles} multiple compact={items.length > 0} accept={mode === "hive" ? HIVE_IMAGE_ACCEPT : undefined} limitLabel={mode === "hive" ? "MAX 25 MB EACH · NO BATCH TOTAL LIMIT" : "MAX 400 KB EACH · 400 KB ON-CHAIN TOTAL"} />{items.length ? <button className="text-button clear-batch" type="button" disabled={hiveBatchUploading} onClick={() => { setItems([]); setMetadataEditors({}); setActiveMetadataItemId(""); clearDestinationCsvImport(); setBulkMetadata(""); }}>CLEAR</button> : null}</> : <label className="field yaml-editor"><span>DELEGATE YAML</span><textarea rows={13} value={delegateYaml} onChange={(event) => { setDelegateYaml(event.target.value); clearDestinationCsvImport(); }} placeholder={`items:\n  - delegate: ${"0".repeat(64)}i0`} /></label>}
          {(mode === "delegates" ? drafts.length : items.length) ? <DestinationCsvImport
            itemCount={mode === "delegates" ? drafts.length : items.length}
            mappingKey={destinationMappingKey}
            appliedCount={destinationCsvRecipients.length}
            onApply={(addresses) => {
              if (mode !== "delegates") {
                setItems((currentItems) => {
                  const currentMappingKey = `${mode}:${currentItems.map((item) => item.id).join(",")}`;
                  return currentItems.length === addresses.length && currentMappingKey === destinationMappingKey
                    ? applyDestinationAddresses(currentItems, addresses)
                    : currentItems;
                });
              }
              setDestinationCsvImport({ mappingKey: destinationMappingKey, addresses });
            }}
            onClear={() => {
              clearDestinationCsvImport();
              if (mode !== "delegates") setItems((current) => current.map((item) => ({ ...item, recipient: "" })));
            }}
          /> : null}
          {mode === "hive" && !config?.hiveMediaEnabled ? <Notice tone="warning">HIVE MEDIA SETUP IN PROGRESS</Notice> : null}
          {mode !== "delegates" && activeMetadataItem && activeMetadataEditor ? <section className="batch-metadata-builder">
            <div className="metadata-builder-heading">
              <div><p className="eyebrow">ITEM METADATA</p><h3>BUILD ONCE. COPY ACROSS.</h3></div>
              <button className="copy-metadata-button" type="button" disabled={items.length < 2} onClick={copyMetadataToOthers}>{metadataCopied ? <Check /> : <Copy />} {metadataCopied ? `COPIED TO ${items.length - 1}` : "COPY TO ALL OTHERS"}</button>
            </div>
            <div className="metadata-item-picker">
              <label className="field"><span>EDITING FILE</span><select value={activeMetadataItem.id} onChange={(event) => { setActiveMetadataItemId(event.target.value); setMetadataCopied(false); }}>
                {items.map((item, index) => <option key={item.id} value={item.id}>{String(index + 1).padStart(2, "0")} · {item.file.name}{batchMetadataEditorHasData(metadataEditors[item.id]) ? " · METADATA ADDED" : ""}</option>)}
              </select></label>
              <span>{activeMetadataIndex + 1} / {items.length}</span>
            </div>
            <div className="metadata-builder-fields">
              <label className="field full"><span>ON-CHAIN METADATA <small>JSON</small></span><textarea rows={5} value={activeMetadataEditor.metadata} onChange={(event) => updateActiveMetadataEditor((current) => ({ ...current, metadata: event.target.value }))} placeholder={'{"name":"Spam #1","collection":"Spam The Chain","edition":1}'} /></label>
              <label className="field full"><span>TITLE</span><input value={activeMetadataEditor.title} onChange={(event) => updateActiveMetadataEditor((current) => ({ ...current, title: event.target.value }))} placeholder="STC #1" /></label>
              <div className="trait-editor full">
                <span className="trait-editor-label">TRAITS</span>
                <div className="trait-list">
                  {activeMetadataEditor.traits.map((row, index) => (
                    <div className="trait-row" key={row.id}>
                      <input aria-label={`Batch trait ${index + 1} name`} value={row.name} onChange={(event) => updateActiveMetadataEditor((current) => ({ ...current, traits: current.traits.map((candidate) => candidate.id === row.id ? { ...candidate, name: event.target.value } : candidate) }))} placeholder="TRAIT NAME" />
                      {row.type === "boolean" ? (
                        <select aria-label={`Batch trait ${index + 1} value`} value={row.value || "true"} onChange={(event) => updateActiveMetadataEditor((current) => ({ ...current, traits: current.traits.map((candidate) => candidate.id === row.id ? { ...candidate, value: event.target.value } : candidate) }))}>
                          <option value="true">TRUE</option><option value="false">FALSE</option>
                        </select>
                      ) : <input aria-label={`Batch trait ${index + 1} value`} disabled={row.type === "null"} inputMode={row.type === "number" ? "numeric" : undefined} value={row.value} onChange={(event) => updateActiveMetadataEditor((current) => ({ ...current, traits: current.traits.map((candidate) => candidate.id === row.id ? { ...candidate, value: event.target.value } : candidate) }))} placeholder={row.type === "null" ? "NULL" : "VALUE"} />}
                      <select aria-label={`Batch trait ${index + 1} type`} value={row.type} onChange={(event) => {
                        const type = event.target.value as TraitInputType;
                        updateActiveMetadataEditor((current) => ({ ...current, traits: current.traits.map((candidate) => candidate.id === row.id ? { ...candidate, type, value: type === "boolean" ? "true" : type === "null" ? "" : candidate.value } : candidate) }));
                      }}>
                        <option value="text">TEXT</option><option value="number">NUMBER</option><option value="boolean">BOOLEAN</option><option value="null">NULL</option>
                      </select>
                      <button className="remove-trait-button" type="button" aria-label={`Remove batch trait ${index + 1}`} onClick={() => updateActiveMetadataEditor((current) => ({ ...current, traits: current.traits.filter((candidate) => candidate.id !== row.id) }))}><X /></button>
                    </div>
                  ))}
                </div>
                <button className="add-trait-button" type="button" onClick={() => updateActiveMetadataEditor((current) => ({ ...current, traits: [...current.traits, newTraitRow()] }))}><Plus /> ADD TRAIT</button>
              </div>
            </div>
            <DataPreview label="ITEM DATA PREVIEW" value={activeMetadataPreview} error={activeMetadataPreviewError || undefined} note="Metadata and properties will remain separate on-chain fields." />
          </section> : null}
          {mode !== "delegates" && items.length ? <section className="bulk-metadata-card">
            <div className="bulk-metadata-heading"><div><p className="eyebrow">BULK METADATA</p><h3>PASTE ONE LINE PER FILE</h3></div><button className="template-button" type="button" disabled={Boolean(bulkMetadata.trim())} onClick={() => setBulkMetadata(buildBulkMetadataTemplate(items.map((item) => item.file.name)))}><Clipboard /> BUILD TEMPLATE</button></div>
            <label className="field yaml-editor"><span>FILENAME | JSON METADATA</span><textarea rows={Math.min(12, Math.max(5, items.length + 1))} value={bulkMetadata} onChange={(event) => setBulkMetadata(event.target.value)} placeholder={'01.png | {"name":"STC #1","collection":"My drop","edition":1}\n02.png | {"name":"STC #2","collection":"My drop","edition":2}\n03.png | {"name":"STC #3","collection":"My drop","edition":3}'} /></label>
            <DataPreview label="BULK METADATA PREVIEW" value={bulkMetadataPreview} error={bulkMetadata.trim() ? yamlError || undefined : undefined} note={drafts.filter((draft) => draft.metadata !== undefined).length > 8 ? `Showing 8 of ${numberFormatter.format(drafts.filter((draft) => draft.metadata !== undefined).length)} metadata records.` : undefined} />
          </section> : null}
          <button className="advanced-toggle" type="button" onClick={() => setAdvanced((open) => !open)}><span><SlidersHorizontal /> OPTIONS</span><ChevronDown className={advanced ? "rotate" : ""} /></button>
          {advanced ? <div className="advanced-stack"><section className="advanced-section"><div><h3>ITEM DATA</h3></div><label className="field full"><span>ADVANCED YAML</span><textarea rows={8} value={itemYaml} onChange={(event) => setItemYaml(event.target.value)} placeholder={`- file: artwork.png\n  recipient: bc1p…\n  metadata:\n    collection: Spam The Chain`} /></label></section>
            <section className="advanced-section rare-sat-section">
              <RareSatPicker {...rareSats.pickerProps} />
            </section>
            <section className="advanced-section"><div><h3>PARENTS</h3></div><div className="advanced-grid"><ParentFields primary={parentId} onPrimary={setParentId} coParents={coParentIds} onCoParents={setCoParentIds} /><label className="field"><span>POSTAGE / ITEM</span><div className="suffix-input"><input type="number" min={DEFAULT_POSTAGE} value={postage} onChange={(event) => setPostage(Math.max(DEFAULT_POSTAGE, Number(event.target.value)))} /><span>SATS</span></div></label></div></section>
            {mode === "hive" ? <section className="advanced-section"><div className="check-row locked-choice"><CheckCircle2 /><span><strong>BROTLI REFERENCES</strong><small>Every tiny Hive reference is compressed automatically.</small></span></div></section> : <section className="advanced-section"><label className="check-row"><input type="checkbox" checked={brotli} onChange={(event) => toggleBrotli(event.target.checked)} /><span><strong>{compressing ? "COMPRESSING…" : "BROTLI"}</strong></span></label></section>}
          </div> : null}
          {yamlError ? <Notice tone="error">{yamlError}</Notice> : null}
          {batchSizeError ? <Notice tone="error">{batchSizeError}</Notice> : null}
          {error ? <Notice tone="error">{error}</Notice> : null}
        </section>
        <SubmitPanel
          drafts={drafts}
          feeRate={feeRate}
          onFeeRate={setFeeRate}
          resolveRevealOptions={resolveRevealOptions}
          title={`${mode === "hive" ? items.length : drafts.length}-item batch`}
          displayCount={mode === "hive" ? items.length : undefined}
          prepareDrafts={mode === "hive" && items.length ? prepareBatchHiveDrafts : undefined}
          preparationProgress={mode === "hive" ? hiveBatchProgress : undefined}
          disabledReason={mode === "hive" && !config?.hiveMediaEnabled ? "Hive media is not configured yet." : yamlError || batchSizeError || compressing ? yamlError || batchSizeError || "Wait for Brotli compression to finish." : mode === "files" && drafts.length !== items.length ? "Wait for every file to finish reading." : undefined}
        />
      </div>
    </main>
  );
}

function Brc20Page() {
  const { fees } = useAppState();
  const [operation, setOperation] = useState<Brc20Operation>("mint");
  const [ticker, setTicker] = useState("");
  const [amount, setAmount] = useState("");
  const [maxSupply, setMaxSupply] = useState("");
  const [mintLimit, setMintLimit] = useState("");
  const [decimals, setDecimals] = useState(18);
  const [repeats, setRepeats] = useState(1);
  const [feeRate, setFeeRate] = useState(12);
  useEffect(() => { if (fees) setFeeRate(fees.halfHourFee); }, [fees]);
  const fields: Brc20Fields = { operation, ticker, amount, maxSupply, mintLimit, decimals };
  let payload = "";
  let payloadError = "";
  let drafts: InscriptionDraft[] = [];
  try { payload = buildBrc20Payload(fields); drafts = createBrc20Drafts(fields, repeats); } catch (caught) { payloadError = caught instanceof Error ? caught.message : "Invalid BRC-20 payload"; }

  return (
    <main className="page-shell brc-page">
      <PageIntro eyebrow="TOKEN // 03" title="BRC‑20" accent="BUILDER." />
      <div className="workspace-grid">
        <section className="maker-card">
          <div className="operation-tabs">
            {(["deploy", "mint", "transfer"] as Brc20Operation[]).map((value) => <button key={value} type="button" className={operation === value ? "active" : ""} onClick={() => setOperation(value)}>{value}</button>)}
          </div>
          <div className="form-grid">
            <label className="field"><span>TICKER</span><input maxLength={4} value={ticker} onChange={(event) => setTicker(event.target.value)} placeholder="ORDI" /></label>
            {operation === "deploy" ? (
              <>
                <label className="field"><span>MAX SUPPLY</span><input inputMode="numeric" value={maxSupply} onChange={(event) => setMaxSupply(event.target.value)} placeholder="21000000" /></label>
                <label className="field"><span>MINT LIMIT</span><input inputMode="numeric" value={mintLimit} onChange={(event) => setMintLimit(event.target.value)} placeholder="1000" /></label>
                <label className="field"><span>DECIMALS</span><input type="number" min="0" max="18" value={decimals} onChange={(event) => setDecimals(Number(event.target.value))} /></label>
              </>
            ) : (
              <label className="field"><span>AMOUNT</span><input inputMode="numeric" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="1000" /></label>
            )}
            <label className="field"><span>REPEAT <small>1–500</small></span><input type="number" min="1" max="500" value={repeats} onChange={(event) => setRepeats(Math.min(500, Math.max(1, Number(event.target.value))))} /></label>
          </div>
          <div className="payload-preview">
            <div><p className="eyebrow">PAYLOAD</p><span>{payload ? bytes(new TextEncoder().encode(payload).length) : "—"}</span></div>
            <pre>{payload || '{\n  "p": "brc-20",\n  "op": "…"\n}'}</pre>
            {payload ? <CopyButton value={payload} label="Copy payload" /> : null}
          </div>
          {payloadError ? <Notice tone="warning">{payloadError}</Notice> : null}
        </section>
        <SubmitPanel drafts={drafts} feeRate={feeRate} onFeeRate={setFeeRate} title={`${ticker.toUpperCase()} ${operation}`} disabledReason={payloadError || undefined} />
      </div>
    </main>
  );
}

function BroadcastPage() {
  const [rawHex, setRawHex] = useState("");
  const [packageValue, setPackageValue] = useState<SignedPackage | null>(null);
  const [status, setStatus] = useState("");
  const [resultTxid, setResultTxid] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const broadcastRaw = async (event: FormEvent) => {
    event.preventDefault(); setBusy(true); setError(""); setStatus("SENDING"); setResultTxid("");
    try { const txid = (await broadcastTransaction(rawHex.trim())).trim(); setResultTxid(txid); setStatus("SENT"); }
    catch (caught) { setStatus(""); setError(caught instanceof Error ? caught.message : "Broadcast failed"); }
    finally { setBusy(false); }
  };
  const loadPackage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; if (!file) return;
    try {
      const parsed = JSON.parse(await file.text()) as SignedPackage;
      if (!parsed.commitHex || !parsed.revealHex || !parsed.commitTxid || !parsed.revealTxid) throw new Error("This is not a Spam The Chain package.");
      setPackageValue(parsed); setError(""); setResultTxid(""); setStatus("");
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not read that package"); }
  };
  const broadcastPackage = async () => {
    if (!packageValue) return; setBusy(true); setError(""); setStatus("CHECKING SAVED TRANSACTIONS"); setResultTxid("");
    try {
      await resumeRecoveryPackage(packageValue);
      setResultTxid(packageValue.revealTxid);
      setStatus("SENT");
    } catch (caught) { setStatus(""); setError(caught instanceof Error ? caught.message : "Package broadcast failed"); }
    finally { setBusy(false); }
  };

  return (
    <main className="page-shell console-page">
      <RecoveryPanel onSelect={(value) => { setPackageValue(value); setError(""); setStatus("PACKAGE LOADED · REVIEW BEFORE SENDING"); }} />
      <PageIntro eyebrow="TOOLS // 04" title="BROADCAST" accent="TRANSACTION." />
      <div className="console-grid">
        <section className="console-card package-card">
          <div className="card-icon"><FileJson /></div><p className="eyebrow">PACKAGE</p><h2>SPAM THE CHAIN JSON</h2>
          <input ref={fileRef} hidden type="file" accept="application/json,.json" onChange={(event) => void loadPackage(event)} />
          <button className="secondary-button wide" type="button" onClick={() => fileRef.current?.click()}><Upload /> CHOOSE FILE</button>
          {packageValue ? (
            <div className="package-details"><div><span>PAYMENT</span><code>{shortId(packageValue.commitTxid)}</code></div><div><span>INSCRIPTION</span><code>{shortId(packageValue.revealTxid)}</code></div><button className="primary-button wide" type="button" disabled={busy} onClick={() => void broadcastPackage()}>{busy ? <LoaderCircle className="spin" /> : <Radio />} RESUME SAVED PACKAGE</button></div>
          ) : null}
        </section>
        <form className="console-card" onSubmit={(event) => void broadcastRaw(event)}>
          <div className="card-icon"><Radio /></div><p className="eyebrow">RAW</p><h2>TRANSACTION HEX</h2>
          <label className="field"><span>HEX</span><textarea rows={8} spellCheck={false} value={rawHex} onChange={(event) => setRawHex(event.target.value)} placeholder="0200000001…" /></label>
          <button className="primary-button wide" type="submit" disabled={busy || rawHex.trim().length < 20}>{busy ? <LoaderCircle className="spin" /> : <Send />} SEND TRANSACTION</button>
        </form>
      </div>
      {status ? <Notice tone={status === "SENT" ? "success" : "neutral"}>{status}</Notice> : null}
      {resultTxid ? <a className="result-link" href={transactionExplorerUrl(resultTxid)} target="_blank" rel="noreferrer">VIEW ON MEMEPOOL.SPACE <ExternalLink /></a> : null}
      {error ? <Notice tone="error">{error}</Notice> : null}
    </main>
  );
}

function CompletionPage() {
  const { completion, setCompletion } = useAppState();
  if (!completion) return <Navigate to="/inscribe" replace />;
  const { package: signed, summary, name, count, broadcast } = completion;
  const commitUrl = transactionExplorerUrl(signed.commitTxid);
  const revealUrl = transactionExplorerUrl(signed.revealTxid);
  const inscriptionIds = buildInscriptionIds(signed.revealTxid, count);
  const allDetails = JSON.stringify({ ...signed, inscriptionIds, summary }, null, 2);
  return (
    <main className="completion-page">
      <section className="completion-hero">
        <div className="success-orbit">{broadcast ? <Check /> : <FileJson />}</div>
        <div><p className="eyebrow">{broadcast ? "INSCRIBED // BITCOIN" : "SIGNED PACKAGE"}</p><h1>{broadcast ? "INSCRIBED." : "NOT SENT."}</h1><p>{name}{count > 1 ? ` + ${count - 1}` : ""}</p></div>
      </section>
      <div className="completion-grid">
        <section className="completion-main">
          <div className="status-panels"><div><span>STATUS</span><strong className={broadcast ? "pending" : "ready"}>{broadcast ? "ACCEPTED · PENDING" : "NOT SENT"}</strong></div><div><span>TOTAL</span><strong>{sats(summary.total)}</strong></div></div>
          <Notice tone={broadcast ? "success" : "warning"}>{broadcast ? "Bitcoin accepted the payment and inscription transactions. Confirmation is still pending." : "This package is signed but has not been sent. The calculated IDs will not appear on Bitcoin until you broadcast it."}</Notice>
          <div className="completion-id-panel">
            <div className="id-panel-heading"><div><p className="eyebrow">INSCRIPTIONS // {count}</p><h2>{broadcast ? "ALL IDS" : "CALCULATED IDS"}</h2></div><button className="secondary-button compact" type="button" onClick={() => void copyText(inscriptionIds.join("\n"), "INSCRIPTION IDS COPIED")}><Copy /> COPY ALL</button></div>
            <div className="inscription-id-list">
              {inscriptionIds.map((id, index) => (
                <div className="inscription-id-row" key={id}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <code title={id}>{id}</code>
                  <CopyButton value={id} label={`Copy inscription ${index + 1}`} />
                  {broadcast ? <a href={inscriptionExplorerUrl(signed.revealTxid, index)} target="_blank" rel="noreferrer" aria-label={`Open inscription ${index + 1} on ordinals.com`}><ExternalLink /></a> : null}
                </div>
              ))}
            </div>
          </div>
          <div className="completion-actions"><Link className="primary-button" to={broadcast ? completion.returnPath ?? "/inscribe" : "/console"} onClick={() => setCompletion(null)}>{broadcast ? "INSCRIBE AGAIN" : "OPEN BROADCAST"} <ArrowRight /></Link>{!broadcast ? <button className="secondary-button" type="button" onClick={() => downloadSignedPackage(signed)}><Download /> DOWNLOAD</button> : null}</div>
        </section>
        <aside className="detail-stack">
          <div className="detail-card"><p className="eyebrow">TRANSACTIONS</p><div><span>REVEAL</span><code>{shortId(signed.revealTxid)}</code><CopyButton value={signed.revealTxid} /></div><div><span>PAYMENT</span><code>{shortId(signed.commitTxid)}</code><CopyButton value={signed.commitTxid} /></div></div>
          <div className="detail-card"><p className="eyebrow">COST</p><FeeSummary summary={summary} count={count} contentBytes={0} feeRate={1} /></div>
        </aside>
      </div>
      <section className="verify-strip">
        <p className="eyebrow">OPEN</p>
        <div>{broadcast ? <><a href={commitUrl} target="_blank" rel="noreferrer"><Radio /> <span><strong>PAYMENT TX</strong><small>MEMEPOOL.SPACE</small></span><ExternalLink /></a><a href={revealUrl} target="_blank" rel="noreferrer"><Gauge /> <span><strong>INSCRIPTION TX</strong><small>MEMEPOOL.SPACE</small></span><ExternalLink /></a><Link to="/history"><HistoryIcon /><span><strong>HISTORY</strong></span><ArrowRight /></Link></> : <Link to="/console"><Radio /><span><strong>BROADCAST</strong></span><ArrowRight /></Link>}<button type="button" onClick={() => void copyText(allDetails, "INSCRIPTION DETAILS COPIED")}><Clipboard /><span><strong>COPY DETAILS</strong></span><Copy /></button></div>
      </section>
    </main>
  );
}

const historyDate = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

async function refreshPendingHistory(address: string, records: InscriptionHistoryRecord[]): Promise<InscriptionHistoryRecord[]> {
  const pendingTxids = records.filter((record) => !record.confirmed).map((record) => record.revealTxid);
  if (pendingTxids.length === 0) return records;
  const statuses = {} as Awaited<ReturnType<typeof fetchInscriptionTransactionStatuses>>;
  for (let index = 0; index < pendingTxids.length; index += 100) {
    Object.assign(statuses, await fetchInscriptionTransactionStatuses(pendingTxids.slice(index, index + 100)));
  }
  const updated = applyInscriptionTransactionStatuses(records, statuses);
  updated.forEach((record) => saveLocalInscriptionHistory(address, record, window.localStorage));
  return updated;
}

function HistoryInscriptionMedia({ inscriptionId, label = "" }: { inscriptionId: string; label?: string }) {
  return <ResilientInscriptionMedia
    inscriptionId={inscriptionId}
    label={label || `Inscription ${inscriptionId} preview`}
    className="history-selection-media"
  />;
}

function HistoryPage() {
  const { wallet, setWalletModalOpen } = useAppState();
  const navigate = useNavigate();
  const [records, setRecords] = useState<InscriptionHistoryRecord[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState("");
  const [selectionMode, setSelectionMode] = useState<"drop" | null>(null);
  const [selectedInscriptionIds, setSelectedInscriptionIds] = useState<Set<string>>(() => new Set());
  const paymentAddress = wallet?.payment.address;
  const selecting = selectionMode !== null;
  const selectingForDrop = selectionMode === "drop";

  useEffect(() => {
    let active = true;
    setCursor(null);
    setError("");
    setSelectionMode(null);
    setSelectedInscriptionIds(new Set());
    if (!paymentAddress) {
      setRecords([]);
      setLoading(false);
      return () => { active = false; };
    }

    const localRecords = readLocalInscriptionHistory(paymentAddress, window.localStorage);
    setRecords(localRecords);
    setLoading(true);
    void (async () => {
      let nextRecords = localRecords;
      let nextCursor: string | null = null;
      try {
        const page = await fetchInscriptionHistory(paymentAddress);
        const excludedHistoryTxids = page.excludedTransactionIds ?? page.excludedRevealTxids ?? [];
        removeLocalInscriptionHistory(paymentAddress, excludedHistoryTxids, window.localStorage);
        page.records.forEach((record) => saveLocalInscriptionHistory(paymentAddress, record, window.localStorage));
        nextRecords = excludeInscriptionHistory(
          mergeInscriptionHistory(nextRecords, page.records),
          excludedHistoryTxids,
        );
        nextCursor = page.nextCursor;
      } catch (caught) {
        if (active) setError(caught instanceof Error ? caught.message : "History could not load.");
      }
      try {
        nextRecords = await refreshPendingHistory(paymentAddress, nextRecords);
      } catch {
        // History remains usable when the live confirmation check is temporarily unavailable.
      }
      if (active) {
        setRecords(nextRecords);
        setCursor(nextCursor);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [paymentAddress]);

  const loadOlder = async () => {
    if (!paymentAddress || !cursor || loadingOlder) return;
    setError("");
    setLoadingOlder(true);
    try {
      const page = await fetchInscriptionHistory(paymentAddress, cursor);
      const excludedHistoryTxids = page.excludedTransactionIds ?? page.excludedRevealTxids ?? [];
      removeLocalInscriptionHistory(paymentAddress, excludedHistoryTxids, window.localStorage);
      page.records.forEach((record) => saveLocalInscriptionHistory(paymentAddress, record, window.localStorage));
      const nextRecords = await refreshPendingHistory(
        paymentAddress,
        excludeInscriptionHistory(
          mergeInscriptionHistory(records, page.records),
          excludedHistoryTxids,
        ),
      );
      setRecords(nextRecords);
      setCursor(page.nextCursor);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Older inscriptions could not load.");
    } finally {
      setLoadingOlder(false);
    }
  };

  const toggleInscription = (inscriptionId: string) => setSelectedInscriptionIds((current) => {
    const next = new Set(current);
    if (next.has(inscriptionId)) next.delete(inscriptionId);
    else next.add(inscriptionId);
    return next;
  });

  const toggleBatch = (record: InscriptionHistoryRecord) => setSelectedInscriptionIds((current) => {
    const next = new Set(current);
    const allSelected = record.inscriptionIds.every((id) => next.has(id));
    record.inscriptionIds.forEach((id) => allSelected ? next.delete(id) : next.add(id));
    return next;
  });

  const continueSelection = () => {
    if (!paymentAddress || selectionMode !== "drop") return;
    try {
      const prefill = createHistoryLaunchpadPrefill(paymentAddress, selectedInscriptionIds);
      window.sessionStorage.setItem(HISTORY_LAUNCHPAD_PREFILL_KEY, JSON.stringify(prefill));
      void navigate("/launchpad/create?source=history");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Those inscriptions could not be selected");
    }
  };

  const verifiedRecords = records.filter((record) => record.confirmed || record.broadcastVerified);
  const loadedIds = verifiedRecords.flatMap((record) => record.inscriptionIds);

  return (
    <main className="page-shell history-page">
      <RecoveryPanel />
      <PageIntro
        eyebrow={selectingForDrop ? "LAUNCHPAD // INVENTORY" : "WALLET // INSCRIPTIONS"}
        title={selecting ? "SELECT" : "WALLET"}
        accent={selectingForDrop ? "INSCRIPTIONS." : "HISTORY."}
      />
      {!wallet ? (
        <section className="history-state">
          <WalletCards />
          <h2>CONNECT WALLET</h2>
          <button className="primary-button" type="button" onClick={() => setWalletModalOpen(true)}>CONNECT <ArrowRight /></button>
        </section>
      ) : (
        <>
          <section className="history-wallet-bar">
            <AccountWalletSelector label="HISTORY WALLET" />
            <span><small>{selecting ? "SELECTED" : "FOUND"}</small><strong>{selecting ? selectedInscriptionIds.size : verifiedRecords.reduce((total, record) => total + record.count, 0)} INSCRIPTIONS</strong></span>
            <div className="history-wallet-actions">
              <button className="secondary-button compact" type="button" disabled={loading || selecting} onClick={() => {
                const localRecords = readLocalInscriptionHistory(wallet.payment.address, window.localStorage);
                setRecords(localRecords);
                setCursor(null);
                setError("");
                setLoading(true);
                void (async () => {
                  try {
                    const page = await fetchInscriptionHistory(wallet.payment.address);
                    const excludedHistoryTxids = page.excludedTransactionIds ?? page.excludedRevealTxids ?? [];
                    removeLocalInscriptionHistory(wallet.payment.address, excludedHistoryTxids, window.localStorage);
                    page.records.forEach((record) => saveLocalInscriptionHistory(wallet.payment.address, record, window.localStorage));
                    const nextRecords = await refreshPendingHistory(
                      wallet.payment.address,
                      excludeInscriptionHistory(
                        mergeInscriptionHistory(localRecords, page.records),
                        excludedHistoryTxids,
                      ),
                    );
                    setRecords(nextRecords);
                    setCursor(page.nextCursor);
                  } catch (caught) {
                    try {
                      setRecords(await refreshPendingHistory(wallet.payment.address, localRecords));
                    } catch {
                      // Keep local history visible if both remote checks are unavailable.
                    }
                    setError(caught instanceof Error ? caught.message : "History could not load.");
                  } finally {
                    setLoading(false);
                  }
                })();
              }}>{loading ? <LoaderCircle className="spin" /> : <HistoryIcon />} REFRESH</button>
              <button className={selecting ? "secondary-button compact" : "primary-button compact"} type="button" onClick={() => {
                setSelectionMode((current) => current === "drop" ? null : "drop");
                setSelectedInscriptionIds(new Set());
                setError("");
              }}>{selectingForDrop ? <X /> : <Layers3 />} {selectingForDrop ? "CANCEL" : "CREATE DROP"}</button>
            </div>
          </section>
          {selecting ? <section className="history-selection-bar">
            <div><span>SELECTED</span><strong>{numberFormatter.format(selectedInscriptionIds.size)}</strong><small>Choose complete batches, individual inscriptions, or any mix.</small></div>
            <div className="history-selection-actions">
              <button type="button" onClick={() => setSelectedInscriptionIds(new Set(loadedIds))}>SELECT ALL LOADED</button>
              <button type="button" disabled={selectedInscriptionIds.size === 0} onClick={() => setSelectedInscriptionIds(new Set())}>CLEAR</button>
              <button className="history-selection-continue" type="button" disabled={selectedInscriptionIds.size === 0} onClick={continueSelection}>CREATE DROP <ArrowRight /></button>
            </div>
          </section> : null}
          {error ? <Notice tone="error">{error}</Notice> : null}
          {loading && records.length === 0 ? (
            <section className="history-state compact-state"><LoaderCircle className="spin" /><h2>CHECKING BITCOIN</h2></section>
          ) : records.length === 0 ? (
            <section className="history-state compact-state"><HistoryIcon /><h2>NO INSCRIPTIONS</h2><Link className="primary-button" to="/inscribe">INSCRIBE <ArrowRight /></Link></section>
          ) : (
            <section className="history-records" aria-label="Spam The Chain inscription history">
              {records.map((record) => {
                const selectedInBatch = record.inscriptionIds.filter((id) => selectedInscriptionIds.has(id)).length;
                const batchSelected = selectedInBatch === record.inscriptionIds.length;
                const batchPartial = selectedInBatch > 0 && !batchSelected;
                const verified = record.confirmed || record.broadcastVerified === true;
                return (
                <article className={`history-record-card${selecting ? " selecting" : ""}${batchSelected ? " selected" : ""}`} key={record.revealTxid}>
                  <header>
                    <div><p className="eyebrow">{record.createdAt ? historyDate.format(record.createdAt).toUpperCase() : "PENDING"}</p><h2>{record.count} {record.count === 1 ? "INSCRIPTION" : "INSCRIPTIONS"}</h2></div>
                    <div className="history-card-actions">
                      <strong className={record.confirmed ? "history-confirmed" : "history-pending"}>{inscriptionHistoryStatus(record)}</strong>
                      {selecting ? <button type="button" disabled={!verified} className={batchSelected || batchPartial ? "history-batch-select active" : "history-batch-select"} onClick={() => toggleBatch(record)}><span>{batchSelected ? <Check /> : batchPartial ? "—" : null}</span>{batchSelected ? "BATCH SELECTED" : batchPartial ? `${selectedInBatch} OF ${record.count}` : "SELECT BATCH"}</button> : null}
                    </div>
                  </header>
                  {!verified ? <Notice tone="warning">This saved transaction has not been verified on Bitcoin. Check both transaction IDs and use <Link to="/console">Transaction Recovery</Link> before creating another inscription.</Notice> : null}
                  {!selecting ? <div className="history-links">
                    <a href={transactionExplorerUrl(record.revealTxid)} target="_blank" rel="noreferrer">INSCRIPTION TX <small>MEMEPOOL.SPACE</small><ExternalLink /></a>
                    <a href={transactionExplorerUrl(record.commitTxid)} target="_blank" rel="noreferrer">PAYMENT TX <small>MEMEPOOL.SPACE</small><ExternalLink /></a>
                  </div> : null}
                  {!selecting ? <><div className="history-id-heading"><p className="eyebrow">ALL IDS</p><button type="button" onClick={() => void copyText(record.inscriptionIds.join("\n"), "INSCRIPTION IDS COPIED")}><Copy /> COPY ALL</button></div>
                  <div className="inscription-id-list history-id-list">
                    {record.inscriptionIds.map((id, index) => (
                      <div className={record.confirmed ? "inscription-id-row history-id-with-media" : "inscription-id-row"} key={id}>
                        {record.confirmed ? <HistoryInscriptionMedia inscriptionId={id} label={`Inscription ${index + 1}`} /> : null}
                        <span>{String(index + 1).padStart(2, "0")}</span>
                        <code title={id}>{id}</code>
                        <CopyButton value={id} label={`Copy inscription ${index + 1}`} />
                        <a href={inscriptionExplorerUrl(record.revealTxid, index)} target="_blank" rel="noreferrer" aria-label={`Open inscription ${index + 1} on ordinals.com`}><ExternalLink /></a>
                      </div>
                    ))}
                  </div></> : <div className="history-selection-grid">
                    {record.inscriptionIds.map((id, index) => <button type="button" disabled={!verified} className={selectedInscriptionIds.has(id) ? "selected" : ""} key={id} onClick={() => toggleInscription(id)} aria-pressed={selectedInscriptionIds.has(id)}>
                      <HistoryInscriptionMedia inscriptionId={id} label={`Inscription ${index + 1}`} />
                      <span className="history-selection-check">{selectedInscriptionIds.has(id) ? <Check /> : null}</span>
                      <strong>#{index + 1}</strong>
                      <code>{id.slice(0, 8)}…i{id.split("i").at(-1)}</code>
                    </button>)}
                  </div>}
                </article>
              );})}
            </section>
          )}
          {cursor ? <button className="secondary-button history-more" type="button" disabled={loadingOlder} onClick={() => void loadOlder()}>{loadingOlder ? <LoaderCircle className="spin" /> : <HistoryIcon />} {records.length > 0 ? "LOAD OLDER" : "SEARCH OLDER"}</button> : null}
        </>
      )}
    </main>
  );
}

const ADMIN_SESSION_STORAGE_KEY = "spam-the-chain:owner-session:v1";

function AdminJobCard({ job }: { job: AdminInscriptionJob }) {
  return (
    <details className="stats-job">
      <summary>
        <span><small>{historyDate.format(job.createdAt).toUpperCase()}</small><strong>{job.count} {job.count === 1 ? "INSCRIPTION" : "INSCRIPTIONS"}</strong></span>
        <span className={job.confirmed ? "history-confirmed" : "history-pending"}>{job.confirmed ? "CONFIRMED" : "PENDING"}</span>
        <ChevronDown />
      </summary>
      <div className="stats-job-links">
        <a href={transactionExplorerUrl(job.revealTxid)} target="_blank" rel="noreferrer">INSCRIPTION TX <ExternalLink /></a>
        <a href={transactionExplorerUrl(job.commitTxid)} target="_blank" rel="noreferrer">PAYMENT TX <ExternalLink /></a>
      </div>
      <div className="history-id-heading"><p className="eyebrow">ALL {job.count} IDS</p><button type="button" onClick={() => void copyText(job.inscriptionIds.join("\n"), "INSCRIPTION IDS COPIED")}><Copy /> COPY ALL</button></div>
      <div className="inscription-id-list stats-id-list">
        {job.inscriptionIds.map((id, index) => (
          <div className="inscription-id-row" key={id}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <code title={id}>{id}</code>
            <CopyButton value={id} label={`Copy inscription ${index + 1}`} />
            <a href={inscriptionExplorerUrl(job.revealTxid, index)} target="_blank" rel="noreferrer" aria-label={`Open inscription ${index + 1} on ordinals.com`}><ExternalLink /></a>
          </div>
        ))}
      </div>
    </details>
  );
}

function StatsPage() {
  const { config, wallet, setWalletModalOpen } = useAppState();
  const [authorization, setAuthorization] = useState("");
  const [stats, setStats] = useState<AdminStatsPage | null>(null);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [walletPage, setWalletPage] = useState<AdminWalletPage | null>(null);
  const [queryInput, setQueryInput] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [error, setError] = useState("");
  const adminAddress = config?.adminAddress ?? "";
  const isAdminWallet = Boolean(wallet && adminAddress && (
    wallet.payment.address.toLowerCase() === adminAddress.toLowerCase()
    || wallet.ordinals.address.toLowerCase() === adminAddress.toLowerCase()
  ));

  useEffect(() => {
    let active = true;
    setStats(null);
    setWalletPage(null);
    setSelectedAddress("");
    setError("");
    if (!isAdminWallet) {
      setAuthorization("");
      setLoading(false);
      return () => { active = false; };
    }
    const stored = window.sessionStorage.getItem(ADMIN_SESSION_STORAGE_KEY) ?? "";
    setAuthorization(stored);
    if (!stored) return () => { active = false; };
    setLoading(true);
    void (async () => {
      try {
        const nextStats = await fetchAdminStats(stored);
        if (!active) return;
        setStats(nextStats);
        const first = nextStats.users[0];
        if (first) {
          const detail = await fetchAdminWallet(stored, first.paymentAddress);
          if (!active) return;
          setSelectedAddress(first.paymentAddress);
          setWalletPage(detail);
        }
      } catch (caught) {
        if (!active) return;
        window.sessionStorage.removeItem(ADMIN_SESSION_STORAGE_KEY);
        setAuthorization("");
        setError(caught instanceof Error && !caught.message.includes("verification required") ? caught.message : "");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [adminAddress, isAdminWallet, wallet]);

  const unlock = async () => {
    if (!wallet || !adminAddress || !isAdminWallet) return;
    setUnlocking(true);
    setError("");
    try {
      const challenge = await requestAdminChallenge(adminAddress);
      const signature = await signWalletOwnershipMessage(wallet, adminAddress, challenge.message);
      const session = await requestAdminSession(challenge.challengeToken, signature);
      window.sessionStorage.setItem(ADMIN_SESSION_STORAGE_KEY, session.authorization);
      setAuthorization(session.authorization);
      const nextStats = await fetchAdminStats(session.authorization);
      setStats(nextStats);
      const first = nextStats.users[0];
      if (first) {
        setSelectedAddress(first.paymentAddress);
        setWalletPage(await fetchAdminWallet(session.authorization, first.paymentAddress));
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Owner verification failed");
    } finally {
      setUnlocking(false);
    }
  };

  const searchUsers = async (event: FormEvent) => {
    event.preventDefault();
    if (!authorization) return;
    const nextQuery = queryInput.trim();
    setQuery(nextQuery);
    setLoading(true);
    setError("");
    try {
      const nextStats = await fetchAdminStats(authorization, nextQuery);
      setStats(nextStats);
      const first = nextStats.users[0];
      if (first) {
        setSelectedAddress(first.paymentAddress);
        setWalletPage(await fetchAdminWallet(authorization, first.paymentAddress));
      } else {
        setSelectedAddress("");
        setWalletPage(null);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Stats could not load");
    } finally {
      setLoading(false);
    }
  };

  const selectUser = async (user: AdminUserRecord) => {
    if (!authorization || user.paymentAddress === selectedAddress) return;
    setSelectedAddress(user.paymentAddress);
    setLoadingJobs(true);
    setError("");
    try {
      setWalletPage(await fetchAdminWallet(authorization, user.paymentAddress));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Wallet activity could not load");
    } finally {
      setLoadingJobs(false);
    }
  };

  return (
    <main className="page-shell stats-page">
      <PageIntro eyebrow="OWNER // APP ACTIVITY" title="APP" accent="STATS." />
      {!wallet ? (
        <section className="history-state"><WalletCards /><h2>CONNECT OWNER WALLET</h2><button className="primary-button" type="button" onClick={() => setWalletModalOpen(true)}>CONNECT <ArrowRight /></button></section>
      ) : !isAdminWallet ? (
        <section className="history-state"><ShieldCheck /><h2>OWNER WALLET REQUIRED</h2><p>Connect {shortenAddress(adminAddress)} to open stats.</p><button className="primary-button" type="button" onClick={() => setWalletModalOpen(true)}>CHANGE WALLET <ArrowRight /></button></section>
      ) : !authorization ? (
        <section className="history-state"><Fingerprint /><h2>VERIFY OWNER</h2><p>Sign once to open private app activity.</p>{error ? <Notice tone="error">{error}</Notice> : null}<button className="primary-button" type="button" disabled={unlocking} onClick={() => void unlock()}>{unlocking ? <LoaderCircle className="spin" /> : <Fingerprint />} VERIFY & OPEN</button></section>
      ) : loading && !stats ? (
        <section className="history-state compact-state"><LoaderCircle className="spin" /><h2>LOADING STATS</h2></section>
      ) : (
        <>
          {error ? <Notice tone="error">{error}</Notice> : null}
          <section className="stats-summary" aria-label="Spam The Chain totals">
            <div><WalletCards /><span><small>USERS</small><strong>{numberFormatter.format(stats?.summary.users ?? 0)}</strong></span></div>
            <div><Layers3 /><span><small>JOBS</small><strong>{numberFormatter.format(stats?.summary.jobs ?? 0)}</strong></span></div>
            <div><FileText /><span><small>INSCRIPTIONS</small><strong>{numberFormatter.format(stats?.summary.inscriptions ?? 0)}</strong></span></div>
            <div><CheckCircle2 /><span><small>CONFIRMED</small><strong>{numberFormatter.format(stats?.summary.confirmedJobs ?? 0)}</strong></span></div>
          </section>
          <form className="stats-search" onSubmit={(event) => void searchUsers(event)}>
            <Search />
            <input value={queryInput} onChange={(event) => setQueryInput(event.target.value)} placeholder="Search wallet, transaction or inscription ID" aria-label="Search app activity" />
            <button className="primary-button compact" type="submit" disabled={loading}>{loading ? <LoaderCircle className="spin" /> : "SEARCH"}</button>
          </form>
          <section className="stats-layout">
            <div className="stats-users">
              <header><p className="eyebrow">{query ? `RESULTS // ${query}` : "ALL USERS"}</p><strong>{numberFormatter.format(stats?.users.length ?? 0)}</strong></header>
              <div className="stats-user-list">
                {stats?.users.map((user) => (
                  <button className={selectedAddress === user.paymentAddress ? "stats-user active" : "stats-user"} type="button" key={user.paymentAddress} onClick={() => void selectUser(user)}>
                    <span><small>PAYMENT</small><code>{shortenAddress(user.paymentAddress)}</code><AdminWalletFeeTotal user={user} /></span>
                    <span><small>INSCRIPTIONS</small><strong>{numberFormatter.format(user.inscriptions)}</strong></span>
                    <span><small>JOBS</small><strong>{numberFormatter.format(user.jobs)}</strong></span>
                    <ArrowRight />
                  </button>
                ))}
                {stats && stats.users.length === 0 ? <div className="stats-empty"><Search /><strong>NO USERS FOUND</strong></div> : null}
              </div>
              {stats?.hasMore ? <button className="secondary-button wide" type="button" disabled={loadingUsers} onClick={() => {
                if (!authorization || !stats) return;
                setLoadingUsers(true);
                void fetchAdminStats(authorization, query, stats.page + 1).then((page) => {
                  setStats((current) => current ? { ...page, users: [...current.users, ...page.users] } : page);
                }).catch((caught) => setError(caught instanceof Error ? caught.message : "More users could not load")).finally(() => setLoadingUsers(false));
              }}>{loadingUsers ? <LoaderCircle className="spin" /> : <HistoryIcon />} LOAD MORE USERS</button> : null}
            </div>
            <div className="stats-wallet-detail">
              {loadingJobs ? <div className="stats-detail-loading"><LoaderCircle className="spin" /></div> : walletPage ? (
                <>
                  <header>
                    <div><p className="eyebrow">SELECTED USER</p><h2>{numberFormatter.format(walletPage.user.inscriptions)} INSCRIPTIONS</h2><AdminWalletFeeTotal user={walletPage.user} /></div>
                    <CopyButton value={walletPage.user.paymentAddress} label="Copy payment address" />
                  </header>
                  <div className="stats-addresses">
                    <span><small>PAYMENT</small><code>{walletPage.user.paymentAddress}</code></span>
                    <span><small>ORDINALS</small><code>{walletPage.user.ordinalsAddress || "NOT RECORDED"}</code></span>
                  </div>
                  <div className="stats-jobs">
                    <p className="stats-fee-note">STC fees only; excludes Bitcoin network fees. Totals include broadcast jobs awaiting confirmation. Historical jobs marked unverified are not assumed to have paid zero.</p>
                    {walletPage.jobs.map((job) => <AdminJobCard job={job} key={job.revealTxid} />)}
                  </div>
                  {walletPage.hasMore ? <button className="secondary-button wide" type="button" disabled={loadingJobs} onClick={() => {
                    if (!authorization || !walletPage) return;
                    setLoadingJobs(true);
                    void fetchAdminWallet(authorization, walletPage.user.paymentAddress, walletPage.page + 1).then((page) => {
                      setWalletPage((current) => current ? { ...page, jobs: [...current.jobs, ...page.jobs] } : page);
                    }).catch((caught) => setError(caught instanceof Error ? caught.message : "More inscriptions could not load")).finally(() => setLoadingJobs(false));
                  }}><HistoryIcon /> LOAD MORE JOBS</button> : null}
                </>
              ) : <div className="stats-empty detail"><WalletCards /><strong>SELECT A USER</strong></div>}
            </div>
          </section>
        </>
      )}
    </main>
  );
}

function Footer() {
  return <footer className="footer"><StcFooterLogo /></footer>;
}

// The native wallet reuses these exact screens with its own in-memory signer
// and router. Other modules retain their existing public routes and provider.
export function WalletToolsRoutes() {
  const { error, loading } = useAppState();
  return <div className="app-frame"><nav className="wallet-tools-nav"><Link to="/inscribe">Inscribe</Link><Link to="/batch">Batch</Link><Link to="/gallery">Gallery</Link><Link to="/tools">UTXO tools</Link><Link to="/history">History</Link></nav>
    {error && <Notice tone="error">{error}</Notice>}
    {loading ? <p role="status">Loading STC tools…</p> : <RouteErrorBoundary><Suspense fallback={<p role="status">Loading tool…</p>}><Routes>
      <Route path="/inscribe" element={<SinglePage />} />
      <Route path="/batch" element={<BatchPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/history" element={<HistoryPage />} />
      <Route path="/complete" element={<CompletionPage />} />
      <Route path="/tools/*" element={<ToolsApp />} />
      <Route path="*" element={<Navigate to="/inscribe" replace />} />
    </Routes></Suspense></RouteErrorBoundary>}
  </div>;
}

export function App() {
  const { error, loading } = useAppState();
  const location = useLocation();
  const isLaunchpad = location.pathname.startsWith("/launchpad");
  const isMarketplace = location.pathname.startsWith("/marketplacehiddenfuckoff") || location.pathname === "/rare-sats" || location.pathname.startsWith("/rare-sats/") || location.pathname === "/bitmaps" || location.pathname.startsWith("/bitmaps/");
  const isFinance = location.pathname.startsWith("/finance") || location.pathname.startsWith("/dex") || location.pathname.startsWith("/freeplaydex");
  const isStudio = location.pathname.startsWith("/studio");
  const isTools = location.pathname.startsWith("/tools");
  const isAuctions = location.pathname.startsWith("/auctions");
  const isPartnerInscriber = location.pathname.startsWith("/partner/144pixels/");
  const isShieldedHeroes = location.pathname === "/shield" || location.pathname.startsWith("/shield/") || location.pathname.startsWith("/shield-orddropz") || location.pathname.startsWith("/shielded-heroes") || location.pathname.startsWith("/shielded-hauntlings") || location.pathname === "/launchpad/shielded";
  const hasFeatureFooter = isLaunchpad || isMarketplace || isFinance || isTools || isAuctions || isPartnerInscriber || isShieldedHeroes;
  return (
    <div className={isPartnerInscriber ? "app-frame partner-inscriber-frame" : isShieldedHeroes ? "app-frame shielded-heroes-frame" : isLaunchpad ? "app-frame launchpad-frame" : isMarketplace ? "app-frame marketplace-frame" : isFinance ? "app-frame finance-frame" : isStudio ? "app-frame studio-frame" : isTools ? "app-frame tools-frame" : isAuctions ? "app-frame auctions-frame" : "app-frame"}>
      {!isPartnerInscriber ? <AppHeader /> : null}
      {error && !isPartnerInscriber ? <div className="global-notice"><Notice tone="error">{error}</Notice></div> : null}
      {loading ? isPartnerInscriber
        ? <div className="app-loader" role="status"><LoaderCircle className="spin" /><span>OPENING INSCRIBER</span></div>
        : <div className="app-loader"><BrandMark /><LoaderCircle className="spin" /><span>LOADING</span></div> : (
        <RouteErrorBoundary key={location.pathname}>
        <Suspense fallback={<main className="workspace-page" role="status">LOADING…</main>}>
        <Routes>
          <Route path="/" element={<HomePage primaryOnly />} />
          <Route path="/homepage" element={<HomePage primaryOnly />} />
          <Route path="/inscribe" element={<InscribeRouteShell><SinglePage /></InscribeRouteShell>} />
          <Route path="/batch" element={<InscribeRouteShell><BatchPage /></InscribeRouteShell>} />
          <Route path="/gallery" element={<InscribeRouteShell><GalleryPage /></InscribeRouteShell>} />
          <Route path="/claim-world-resource" element={<TemporaryRuneClaimPage />} />
          <Route path="/runes" element={<InscribeRouteShell><RunesPage /></InscribeRouteShell>} />
          <Route path="/burn" element={<Navigate to="/tools/burn" replace />} />
          <Route path="/brc20" element={<Brc20Page />} />
          <Route path="/history" element={<InscribeRouteShell><HistoryPage /></InscribeRouteShell>} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/admin/feature-tweets" element={<FeatureTweetAdminPage />} />
          <Route path="/partner/144pixels/inscribe" element={<PartnerInscriberPage />} />
          <Route path="/partner/144pixels/docs" element={<PartnerInscriberDocsPage />} />
          <Route path="/partner/144pixels/theme" element={<PartnerInscriberThemePage />} />
          <Route path="/privacy-is-gay" element={<ShieldedOrdinalMintPage />} />
          <Route path="/shield" element={<ShieldHubPage />} />
          <Route path="/shield-orddropz/*" element={<OrdDropzApp />} />
          <Route path="/shield/about" element={<ShieldedAboutPage />} />
          <Route path="/shield/about/technical" element={<ShieldedTechnicalPage />} />
          <Route path="/shield/node" element={<ShieldedNodePage />} />
          <Route path="/shielded-heroes/*" element={<ShieldedHeroesApp />} />
          <Route path="/shielded-hauntlings/*" element={<ShieldedHauntlingsApp />} />
          <Route path="/shielded-heroes-v2/*" element={<Navigate to="/shielded-hauntlings" replace />} />
          <Route path="/console" element={<InscribeRouteShell><BroadcastPage /></InscribeRouteShell>} />
          <Route path="/complete" element={<CompletionPage />} />
          <Route path="/launchpad/*" element={<LaunchpadApp />} />
          <Route path="/studio/*" element={<StudioApp />} />
          <Route path="/marketplacehiddenfuckoff/*" element={<MarketplaceApp />} />
          <Route path="/rare-sats/*" element={<RareSatsApp />} />
          <Route path="/bitmaps/*" element={<BitmapApp renderReview={props => <SubmitPanel {...props} />} />} />
          <Route path="/finance/*" element={<FinanceApp />} />
          <Route path="/dex" element={<DexApp />} />
          <Route path="/dex/:marketId" element={<DexApp />} />
          <Route path="/freeplaydex" element={<FreeplayDexApp />} />
          <Route path="/freeplaydex/:marketId" element={<FreeplayDexApp />} />
          <Route path="/auctions/*" element={<AuctionsApp />} />
          <Route path="/tools/*" element={<ToolsApp />} />
          <Route path={WORLDS_ROUTE} element={<WorldsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      )}
      {!hasFeatureFooter ? <Footer /> : null}
      <WalletModal />
    </div>
  );
}
